import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import SystemStatusTab from "./StatusWidget";
import ExpensesTab from "./ExpensesTab";
import { useAuth } from "./AuthContext";
import { LocLabel } from "./LocationIcons";
import {
  IcChair, IcBox, IcFolder, IcStar, IcMoney, IcUsers, IcSettings, IcMonitor,
  IcBell, IcCoffee, IcChart, IcMemo, IcWarning, IcTrash, IcClipboard, IcSave,
  IcClock, IcEdit, IcUnlock, IcLock, IcCamera, IcQr, IcRefresh, ProductGlyph,
  IcPalette, IcVolume, IcMail, IcCart, IcKey, IcInfo, IcSearch, IcSparkle,
  IcLink, IcWrench, IcUser, IcScale,
} from "./Icons";

// "" → relative URL: dev'de CRA proxy (package.json "proxy"), prod'da Nginx same-origin
const API = process.env.REACT_APP_API_URL ?? "";

// ── Section Header ────────────────────────────────────────────────────────────
function SectionTitle({ children, action }) {
  return (
    <div className="section-title">
      <span>{children}</span>
      {action}
    </div>
  );
}

// ── Products Tab ──────────────────────────────────────────────────────────────
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [adding, setAdding] = useState(false);
  const [newProduct, setNewProduct] = useState({ category_id: 1, name: "", description: "", price: "", cost: "", image_emoji: "☕", is_weight_based: 0, taste_notes: "", allergens: "" });
  const [filterStatus, setFilterStatus] = useState("active"); // "active" | "passive" | "all"
  const [movingId, setMovingId] = useState(null); // reorder işlemi sırasında UI feedback
  const [sortMode, setSortMode] = useState(false); // sıralama modu
  const [deactivateConfirm, setDeactivateConfirm] = useState(null); // product | null
  const [deleteConfirm, setDeleteConfirm] = useState(null); // product object | null
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [weightConfirm, setWeightConfirm] = useState(false); // gramaj toggle onay modalı
  const [uploadingId, setUploadingId] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [imageSuccess, setImageSuccess] = useState({});
  const [photoMode, setPhotoMode] = useState(false);
  const [brokenImages, setBrokenImages] = useState({});

  // Kategori yönetimi
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catAdding, setCatAdding] = useState(false);
  const [catConfirmAdd, setCatConfirmAdd] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", emoji: "📦", subtitle: "" });
  const [catEditId, setCatEditId] = useState(null);
  const [catEditData, setCatEditData] = useState({});
  const [catDeleteConfirm, setCatDeleteConfirm] = useState(null); // kategori objesi
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState("");

  const load = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        fetch(`${API}/api/admin/products`, { credentials: 'include' }),
        fetch(`${API}/api/admin/categories`, { credentials: 'include' }),
      ]);
      const pJson = pRes.ok  ? await pRes.json().catch(() => ({})) : {};
      const cJson = cRes.ok  ? await cRes.json().catch(() => ({})) : {};
      setProducts(pJson.products   || []);
      setCategories(cJson.categories || []);
    } catch (e) {
      console.error("Products load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => {
    setEditingId(p.id);
    let _tn = "";
    try { _tn = (JSON.parse(p.taste_notes || "[]") || []).join(", "); } catch { _tn = ""; }
    setEditData({ name: p.name, description: p.description, price: p.price, cost: p.cost, image_emoji: p.image_emoji, is_active: p.is_active, is_weight_based: p.is_weight_based || 0, category_id: p.category_id, bean_selectable: p.bean_selectable ? 1 : 0, taste_notes: _tn, allergens: p.allergens || "" });
  };

  const saveEdit = async (id) => {
    await fetch(`${API}/api/admin/products/${id}`, {
      method: "PATCH",
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...editData, price: Number(editData.price), cost: Number(editData.cost),
        taste_notes: (editData.taste_notes || "").split(",").map(s => s.trim()).filter(Boolean),
        allergens: (editData.allergens || "").trim() || null,
      }),
    });
    setEditingId(null);
    load();
  };

  const toggleActive = async (p) => {
    await fetch(`${API}/api/admin/products/${p.id}`, {
      method: "PATCH",
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_active: p.is_active === 1 ? 0 : 1 }),
    });
    load();
  };

  // ↑/↓ sıralama — kategorideki ürünlerin sort_order'ını günceller
  const moveProduct = async (catId, idx, dir) => {
    // Tüm ürünleri (filtre gözetmeksizin) sırala
    const catProds = products
      .filter(p => p.category_id === catId)
      .slice()
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.id - b.id);

    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= catProds.length) return;

    const moved = catProds[idx];
    const other = catProds[targetIdx];
    setMovingId(moved.id);

    // Temiz sort_order ata (0, 10, 20 …)
    const reordered = [...catProds];
    reordered[idx]       = other;
    reordered[targetIdx] = moved;
    const updates = reordered.map((p, i) => ({ id: p.id, sort_order: i * 10 }));

    // Optimistic update
    setProducts(ps => ps.map(x => {
      const u = updates.find(u => u.id === x.id);
      return u ? { ...x, sort_order: u.sort_order } : x;
    }));

    // İki değişen ürünü kaydet
    await Promise.all(
      [{ id: moved.id, sort_order: targetIdx * 10 }, { id: other.id, sort_order: idx * 10 }]
        .map(u => fetch(`${API}/api/admin/products/${u.id}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sort_order: u.sort_order }),
        }))
    );
    setMovingId(null);
  };

  const saveNew = async () => {
    if (!newProduct.name || !newProduct.price) return;
    await fetch(`${API}/api/admin/products`, {
      method: "POST",
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...newProduct, price: Number(newProduct.price), cost: Number(newProduct.cost || 0),
        taste_notes: (newProduct.taste_notes || "").split(",").map(s => s.trim()).filter(Boolean),
        allergens: (newProduct.allergens || "").trim() || null,
      }),
    });
    setAdding(false);
    setNewProduct({ category_id: 1, name: "", description: "", price: "", cost: "", image_emoji: "☕", is_weight_based: 0 });
    load();
  };

  const handleDelete = async () => {
    if (!deleteConfirm || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const res = await fetch(`${API}/api/admin/products/${deleteConfirm.id}`, {
        method: "DELETE",
        credentials: 'include',
      });
      if (res.ok) {
        setDeleteConfirm(null);
        load();
      } else {
        const json = await res.json().catch(() => ({}));
        setDeleteError(json.detail || "Silme başarısız");
      }
    } catch {
      setDeleteError("Bağlantı hatası — backend çalışıyor mu?");
    }
    setDeleting(false);
  };

  const _clearImageMsg = (productId, delay = 3000) => {
    setTimeout(() => {
      setImageErrors(e  => ({ ...e,  [productId]: null }));
      setImageSuccess(s => ({ ...s, [productId]: null }));
    }, delay);
  };

  // ── Kategori CRUD ─────────────────────────────────────────────────────────
  const saveNewCat = async () => {
    if (!newCat.name.trim()) return;
    setCatSaving(true); setCatError(""); setCatConfirmAdd(false);
    try {
      const res = await fetch(`${API}/api/admin/categories`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newCat.name.trim(), emoji: newCat.emoji || "📦", subtitle: (newCat.subtitle || "").trim() }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setCatError(d.detail || "Hata"); return; }
      setNewCat({ name: "", emoji: "📦" });
      setCatAdding(false);
      await load();
    } catch { setCatError("Bağlantı hatası"); }
    finally { setCatSaving(false); }
  };

  const moveCat = async (idx, dir) => {
    const sorted = categories.slice().sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.id - b.id);
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;

    const moved = sorted[idx];
    const other = sorted[targetIdx];

    const reordered = [...sorted];
    reordered[idx]       = other;
    reordered[targetIdx] = moved;
    const updates = reordered.map((c, i) => ({ id: c.id, sort_order: i * 10 }));

    // Optimistic update
    setCategories(cs => cs.map(c => {
      const u = updates.find(u => u.id === c.id);
      return u ? { ...c, sort_order: u.sort_order } : c;
    }));

    await Promise.all(
      [{ id: moved.id, sort_order: targetIdx * 10 }, { id: other.id, sort_order: idx * 10 }]
        .map(u => fetch(`${API}/api/admin/categories/${u.id}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sort_order: u.sort_order }),
        }))
    );
  };

  const saveCatEdit = async (id) => {
    if (!catEditData.name?.trim()) return;
    setCatSaving(true); setCatError("");
    try {
      const res = await fetch(`${API}/api/admin/categories/${id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: catEditData.name.trim(), emoji: catEditData.emoji || "📦" }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setCatError(d.detail || "Hata"); return; }
      setCatEditId(null);
      await load();
    } catch { setCatError("Bağlantı hatası"); }
    finally { setCatSaving(false); }
  };

  const deleteCategory = async (id) => {
    setCatSaving(true); setCatError("");
    try {
      const res = await fetch(`${API}/api/admin/categories/${id}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setCatError(d.detail || "Silinemedi"); return; }
      setCatDeleteConfirm(null);
      setCatError("");
      await load();
    } catch { setCatError("Bağlantı hatası"); }
    finally { setCatSaving(false); }
  };

  const uploadProductImage = async (productId, file) => {
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setImageErrors(e => ({ ...e, [productId]: "Sadece jpg, png, webp kabul edilir" }));
      _clearImageMsg(productId);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageErrors(e => ({ ...e, [productId]: "Dosya 5MB'dan büyük olamaz" }));
      _clearImageMsg(productId);
      return;
    }
    setUploadingId(productId);
    setImageErrors(e  => ({ ...e, [productId]: null }));
    setImageSuccess(s => ({ ...s, [productId]: null }));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API}/api/admin/products/${productId}/image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setImageErrors(e => ({ ...e, [productId]: d.detail || "Yükleme başarısız" }));
      } else {
        setImageSuccess(s => ({ ...s, [productId]: "Yükleme başarılı ✓" }));
        setBrokenImages(b => ({ ...b, [productId]: false }));
        await load();
      }
    } catch {
      setImageErrors(e => ({ ...e, [productId]: "Bağlantı hatası" }));
    } finally {
      setUploadingId(null);
      _clearImageMsg(productId);
    }
  };

  const deleteProductImage = async (productId) => {
    setUploadingId(productId);
    setImageSuccess(s => ({ ...s, [productId]: null }));
    try {
      const res = await fetch(`${API}/api/admin/products/${productId}/image`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setImageSuccess(s => ({ ...s, [productId]: "Görsel kaldırıldı ✓" }));
        await load();
        _clearImageMsg(productId);
      } else {
        setImageErrors(e => ({ ...e, [productId]: "Kaldırılamadı" }));
        _clearImageMsg(productId);
      }
    } catch {
      setImageErrors(e => ({ ...e, [productId]: "Bağlantı hatası" }));
      _clearImageMsg(productId);
    }
    finally { setUploadingId(null); }
  };

  // Durum filtresi — sadece görüntülemeyi etkiler, sıralamayı etkilemez
  const statusFilter = (p) =>
    filterStatus === "all" ||
    (filterStatus === "active"  ? p.is_active === 1 : p.is_active === 0);

  if (loading) return <div className="tab-loading">Yükleniyor…</div>;

  return (
    <div className="tab-content">

      {/* ── Kategorileri Yönet Butonu ── */}
      <div style={{ marginBottom: 12 }}>
        <button
          onClick={() => { setCatModalOpen(true); setCatAdding(false); setCatEditId(null); setCatError(""); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "9px 18px", borderRadius: 10, cursor: "pointer",
            border: "1px solid var(--border)", background: "var(--surface2)",
            fontSize: 13, fontWeight: 500, color: "var(--text2)",
          }}
        >
          <IcFolder size={16} /> Kategorileri Yönet
          <span style={{ background: "var(--border)", borderRadius: 20, padding: "1px 8px", fontSize: 11, color: "var(--text3)" }}>
            {categories.length}
          </span>
        </button>
      </div>

      {/* ── Kategori Yönetim Modalı ── */}
      {catModalOpen && (
        <div
          onClick={() => { setCatModalOpen(false); setCatAdding(false); setCatEditId(null); setCatError(""); }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex",
                   alignItems:"center", justifyContent:"center", zIndex:900, backdropFilter:"blur(4px)" }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background:"var(--card,#fff)", borderRadius:20, width:"92%", maxWidth:480,
            maxHeight:"80vh", display:"flex", flexDirection:"column",
            boxShadow:"0 12px 40px rgba(0,0,0,0.25)", border:"1px solid var(--border)",
          }}>
            {/* Modal header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                          padding:"18px 20px 14px", borderBottom:"1px solid var(--border)", flexShrink:0 }}>
              <span style={{ fontSize:16, fontWeight:700, color:"var(--text)", display:"inline-flex", alignItems:"center", gap:8 }}><IcFolder size={17} /> Kategoriler</span>
              <button onClick={() => { setCatModalOpen(false); setCatAdding(false); setCatEditId(null); setCatError(""); }}
                style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"var(--text2)",
                         width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:8 }}>
                ✕
              </button>
            </div>

            {/* Hata mesajı */}
            {catError && (
              <div style={{ margin:"12px 20px 0", padding:"10px 14px", borderRadius:8, fontSize:13,
                            background:"#fdecea", color:"#c62828", border:"1px solid #f5c6cb" }}>
                {catError}
              </div>
            )}

            {/* Kategori listesi */}
            <div style={{ overflowY:"auto", flex:1, padding:"8px 0" }}>
              {categories
                .slice()
                .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.id - b.id)
                .map((c, idx, sorted) => (
                <div key={c.id} style={{ padding:"0 16px" }}>
                  {catEditId === c.id ? (
                    /* Düzenleme satırı */
                    <div style={{ display:"flex", alignItems:"center", gap:8, minHeight:56,
                                  borderBottom:"1px solid var(--border)", paddingBottom:8, paddingTop:8 }}>
                      <input value={catEditData.emoji}
                        onChange={e => setCatEditData(d => ({ ...d, emoji: e.target.value }))}
                        style={{ width:48, textAlign:"center", fontSize:20, border:"1px solid var(--border)",
                                 borderRadius:8, padding:"6px 4px", background:"var(--surface)" }} />
                      <input value={catEditData.name} autoFocus
                        onChange={e => setCatEditData(d => ({ ...d, name: e.target.value }))}
                        onKeyDown={e => { if (e.key==="Enter") saveCatEdit(c.id); if (e.key==="Escape") setCatEditId(null); }}
                        style={{ flex:1, fontSize:14, border:"1px solid var(--border)", borderRadius:8,
                                 padding:"8px 10px", background:"var(--surface)" }} />
                      <button onClick={() => saveCatEdit(c.id)} disabled={catSaving}
                        style={{ minWidth:44, minHeight:44, borderRadius:8, border:"none",
                                 background:"var(--mint,#4E7C62)", color:"#fff", fontSize:15, cursor:"pointer" }}>
                        {catSaving ? "…" : "✓"}
                      </button>
                      <button onClick={() => { setCatEditId(null); setCatError(""); }}
                        style={{ minWidth:44, minHeight:44, borderRadius:8, border:"1px solid var(--border)",
                                 background:"var(--surface)", fontSize:13, cursor:"pointer", color:"var(--text2)" }}>
                        İptal
                      </button>
                    </div>
                  ) : (
                    /* Normal satır */
                    <div style={{ display:"flex", alignItems:"center", gap:8, minHeight:56,
                                  borderBottom:"1px solid var(--border)" }}>
                      <span style={{ fontSize:22, width:32, textAlign:"center", flexShrink:0 }}>{c.emoji}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:500, color:"var(--text)" }}>{c.name}</div>
                        <div style={{ fontSize:11, color:"var(--text3)", marginTop:1 }}>
                          {products.filter(p => p.category_id === c.id).length} ürün
                        </div>
                      </div>
                      {/* ↑/↓ sıralama */}
                      <div className="sort-pill" style={{ flexShrink:0 }}>
                        <button
                          className="sort-pill-btn"
                          onClick={() => moveCat(idx, -1)}
                          disabled={idx === 0}
                          title="Yukarı taşı"
                        >
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1.5 7L5 3l3.5 4"/>
                          </svg>
                        </button>
                        <button
                          className="sort-pill-btn"
                          onClick={() => moveCat(idx, +1)}
                          disabled={idx === sorted.length - 1}
                          title="Aşağı taşı"
                        >
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1.5 3L5 7l3.5-4"/>
                          </svg>
                        </button>
                      </div>
                      <button
                        onClick={() => { setCatEditId(c.id); setCatEditData({ name:c.name, emoji:c.emoji }); setCatError(""); }}
                        style={{ minWidth:40, minHeight:40, borderRadius:8, border:"1px solid var(--border)",
                                 background:"var(--surface)", fontSize:16, cursor:"pointer", color:"var(--text2)",
                                 display:"flex", alignItems:"center", justifyContent:"center" }}>
                        ✎
                      </button>
                      <button
                        onClick={() => { setCatDeleteConfirm(c); setCatError(""); }}
                        style={{ minWidth:40, minHeight:40, borderRadius:8, border:"1px solid var(--border)",
                                 background:"var(--surface)", fontSize:16, cursor:"pointer", color:"var(--text2)",
                                 display:"flex", alignItems:"center", justifyContent:"center" }}>
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Yeni kategori formu */}
            <div style={{ borderTop:"1px solid var(--border)", padding:"14px 16px", flexShrink:0 }}>
              {catAdding ? (
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <input value={newCat.emoji} onChange={e => setNewCat(d => ({ ...d, emoji: e.target.value }))}
                    placeholder="📦"
                    style={{ width:48, textAlign:"center", fontSize:20, border:"1px solid var(--border)",
                             borderRadius:8, padding:"8px 4px", background:"var(--surface)" }} />
                  <input value={newCat.name} autoFocus
                    onChange={e => setNewCat(d => ({ ...d, name: e.target.value }))}
                    placeholder="Kategori adı"
                    onKeyDown={e => { if (e.key==="Enter" && newCat.name.trim()) setCatConfirmAdd(true); if (e.key==="Escape") { setCatAdding(false); setCatError(""); } }}
                    style={{ flex:1, fontSize:14, border:"1px solid var(--border)", borderRadius:8,
                             padding:"8px 10px", background:"var(--surface)" }} />
                  <button
                    onClick={() => { if (newCat.name.trim()) setCatConfirmAdd(true); }}
                    disabled={!newCat.name.trim()}
                    style={{ minHeight:44, padding:"0 16px", borderRadius:8, border:"none",
                             background:"var(--mint,#4E7C62)", color:"#fff", fontSize:13, fontWeight:600,
                             cursor: newCat.name.trim() ? "pointer" : "not-allowed", opacity: newCat.name.trim() ? 1 : 0.5 }}>
                    Ekle
                  </button>
                  <button onClick={() => { setCatAdding(false); setCatError(""); setNewCat({ name:"", emoji:"📦", subtitle:"" }); }}
                    style={{ minHeight:44, padding:"0 14px", borderRadius:8, border:"1px solid var(--border)",
                             background:"var(--surface)", fontSize:13, cursor:"pointer", color:"var(--text2)" }}>
                    İptal
                  </button>
                  <input value={newCat.subtitle} onChange={e => setNewCat(d => ({ ...d, subtitle: e.target.value }))}
                    placeholder="Alt başlık (opsiyonel — premium menüde bölüm açıklaması)"
                    style={{ flexBasis:"100%", fontSize:13, border:"1px solid var(--border)", borderRadius:8,
                             padding:"8px 10px", background:"var(--surface)", color:"var(--text)" }} />
                </div>
              ) : (
                <button
                  onClick={() => { setCatAdding(true); setCatError(""); setNewCat({ name:"", emoji:"📦", subtitle:"" }); }}
                  style={{ width:"100%", minHeight:44, borderRadius:10, border:"1.5px dashed var(--border)",
                           background:"transparent", fontSize:14, cursor:"pointer", color:"var(--text2)", fontWeight:500 }}>
                  + Yeni Kategori
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Kategori Silme Onay Modalı ── */}
      {catDeleteConfirm && createPortal(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex",
                      alignItems:"center", justifyContent:"center", zIndex:9999, backdropFilter:"blur(6px)",
                      padding:"0 20px" }}>
          <div style={{ background:"var(--surface2)", borderRadius:20, padding:"28px 20px 20px",
                        width:"100%", maxWidth:360, boxShadow:"0 16px 48px rgba(0,0,0,0.35)",
                        border:"1.5px solid var(--border)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:"var(--text)", marginBottom:8 }}>Kategoriyi Sil</div>
            <div style={{ fontSize:14, color:"var(--text2)", lineHeight:1.6, marginBottom:4 }}>
              <strong style={{ color:"var(--text)" }}>{catDeleteConfirm.emoji} {catDeleteConfirm.name}</strong> silinecektir.
            </div>
            <div style={{ fontSize:13, color:"var(--text3)", marginBottom:20 }}>
              Bu kategoride ürün varsa silme engellenir.
            </div>
            {catError && (
              <div style={{ padding:"10px 12px", borderRadius:8, fontSize:13, marginBottom:14,
                            background:"#fdecea", color:"#c62828", border:"1px solid #f5c6cb" }}>
                {catError}
              </div>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <button onClick={() => deleteCategory(catDeleteConfirm.id)} disabled={catSaving}
                style={{ display:"block", width:"100%", minHeight:52, borderRadius:12, border:"none",
                         background:"var(--red,#B85555)", color:"#fff", fontSize:15, fontWeight:600,
                         cursor: catSaving ? "not-allowed" : "pointer", opacity: catSaving ? 0.7 : 1 }}>
                {catSaving ? "Siliniyor…" : "Evet, Sil"}
              </button>
              <button onClick={() => { setCatDeleteConfirm(null); setCatError(""); }}
                style={{ display:"block", width:"100%", minHeight:52, borderRadius:12,
                         border:"1.5px solid var(--border)", background:"var(--surface3)",
                         fontSize:15, cursor:"pointer", color:"var(--text2)", fontWeight:500 }}>
                Vazgeç
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Kategori Ekleme Onay Modalı ── */}
      {catConfirmAdd && createPortal(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex",
                      alignItems:"center", justifyContent:"center", zIndex:9999, backdropFilter:"blur(6px)",
                      padding:"0 20px" }}>
          <div style={{ background:"var(--surface2)", borderRadius:20, padding:"28px 20px 20px",
                        width:"100%", maxWidth:360, boxShadow:"0 16px 48px rgba(0,0,0,0.35)",
                        border:"1.5px solid var(--border)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:"var(--text)", marginBottom:8 }}>Kategori Ekle</div>
            <div style={{ fontSize:14, color:"var(--text2)", lineHeight:1.6, marginBottom:24 }}>
              <strong style={{ color:"var(--text)" }}>{newCat.emoji} {newCat.name}</strong> kategorisi eklenecektir. Emin misiniz?
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <button onClick={saveNewCat} disabled={catSaving}
                style={{ display:"block", width:"100%", minHeight:52, borderRadius:12, border:"none",
                         background:"var(--mint,#4E7C62)", color:"#fff", fontSize:15, fontWeight:600,
                         cursor: catSaving ? "not-allowed" : "pointer", opacity: catSaving ? 0.7 : 1 }}>
                {catSaving ? "Ekleniyor…" : "Evet, Ekle"}
              </button>
              <button onClick={() => setCatConfirmAdd(false)}
                style={{ display:"block", width:"100%", minHeight:52, borderRadius:12,
                         border:"1.5px solid var(--border)", background:"var(--surface3)",
                         fontSize:15, cursor:"pointer", color:"var(--text2)", fontWeight:500 }}>
                Vazgeç
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="filter-bar">
        <span style={{ fontSize: 12, color: "var(--text2)", alignSelf: "center", marginRight: 4 }}>Göster:</span>
        {[
          ["active",  `Aktif (${products.filter(p => p.is_active === 1).length})`],
          ["passive", `Pasif (${products.filter(p => p.is_active === 0).length})`],
          ["all",     `Tümü (${products.length})`],
        ].map(([val, label]) => (
          <button
            key={val}
            className={`filter-btn${filterStatus === val ? " active" : ""}`}
            onClick={() => setFilterStatus(val)}
          >{label}</button>
        ))}
      </div>

      <SectionTitle action={
        <button className="add-btn" onClick={() => setAdding(true)}>+ Ürün Ekle</button>
      }>
        Ürünler
      </SectionTitle>

      {adding && (
        <div className="add-form">
          <div className="add-form-title">Yeni Ürün</div>
          <div className="form-row">
            <select value={newProduct.category_id} onChange={e => setNewProduct({...newProduct, category_id: Number(e.target.value)})}>
              {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
            </select>
            <input placeholder="Emoji" value={newProduct.image_emoji} onChange={e => setNewProduct({...newProduct, image_emoji: e.target.value})} style={{width: 64}} />
          </div>
          <div className="form-row">
            <input placeholder="Ürün adı *" value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})} style={{flex:2}} />
            <input placeholder="Açıklama" value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} style={{flex:2}} />
          </div>
          <div className="form-row">
            <input placeholder="Tat notları (virgülle: karamelize, vanilya)" value={newProduct.taste_notes} onChange={e => setNewProduct({...newProduct, taste_notes: e.target.value})} style={{flex:2}} />
            <input placeholder="Alerjen (opsiyonel: süt, yumurta)" value={newProduct.allergens} onChange={e => setNewProduct({...newProduct, allergens: e.target.value})} style={{flex:2}} />
          </div>
          <div className="form-row">
            <div className="input-group"><span>Fiyat ₺</span><input type="number" placeholder="0" value={newProduct.price} onChange={e => setNewProduct({...newProduct, price: e.target.value})} /></div>
            <div className="input-group"><span>Maliyet ₺</span><input type="number" placeholder="0" value={newProduct.cost} onChange={e => setNewProduct({...newProduct, cost: e.target.value})} /></div>
          </div>
          <div className="form-row" style={{ alignItems: "center", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={newProduct.is_weight_based === 1}
                onChange={e => setNewProduct({...newProduct, is_weight_based: e.target.checked ? 1 : 0})}
              />
              Gramaj bazlı fiyat (fiyat = 100g baz fiyatı)
            </label>
          </div>
          <div className="form-actions">
            <button className="btn-save" onClick={saveNew}>Kaydet</button>
            <button className="btn-cancel" onClick={() => setAdding(false)}>İptal</button>
          </div>
        </div>
      )}

      {deactivateConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999,
        }}>
          <div style={{
            background: "var(--surface)", border: "1.5px solid var(--border)",
            borderRadius: 12, padding: "28px 28px 22px", maxWidth: 380, width: "90%",
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text1)", marginBottom: 10 }}>
              Ürünü pasife al
            </div>
            <div style={{ fontSize: 14, color: "var(--text2)", marginBottom: 18 }}>
              <strong>{deactivateConfirm.image_emoji} {deactivateConfirm.name}</strong> ürünü müşteri menüsünden kaldırılacak ve sipariş alınamayacak. İstediğiniz zaman yeniden aktif edebilirsiniz.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeactivateConfirm(null)}
                style={{
                  background: "var(--surface2)", border: "1px solid var(--border)",
                  borderRadius: 8, color: "var(--text1)", padding: "9px 18px",
                  cursor: "pointer", fontSize: 14,
                }}
              >İptal</button>
              <button
                onClick={() => { toggleActive(deactivateConfirm); setDeactivateConfirm(null); }}
                style={{
                  background: "var(--accent, #7c9a6a)", border: "none", borderRadius: 8,
                  color: "#fff", padding: "9px 18px", cursor: "pointer",
                  fontSize: 14, fontWeight: 600,
                }}
              >Pasife Al</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999,
        }}>
          <div style={{
            background: "var(--surface)", border: "1.5px solid var(--border)",
            borderRadius: 12, padding: "28px 28px 22px", maxWidth: 380, width: "90%",
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text1)", marginBottom: 10 }}>
              Ürünü sil
            </div>
            <div style={{ fontSize: 14, color: "var(--text2)", marginBottom: 18 }}>
              <strong>{deleteConfirm.image_emoji} {deleteConfirm.name}</strong> ürünü geçmiş siparişlerde kullanılmamıştır. Veritabanından kalıcı olarak silinecek, bu işlem geri alınamaz.
            </div>
            {deleteError && (
              <div style={{ fontSize: 13, color: "#e57373", marginBottom: 12 }}>{deleteError}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteError(""); }}
                style={{
                  background: "var(--surface2)", border: "1px solid var(--border)",
                  borderRadius: 8, color: "var(--text1)", padding: "9px 18px",
                  cursor: "pointer", fontSize: 14,
                }}
              >İptal</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  background: "#e57373", border: "none", borderRadius: 8,
                  color: "#fff", padding: "9px 18px", cursor: deleting ? "not-allowed" : "pointer",
                  fontSize: 14, fontWeight: 600,
                }}
              >{deleting ? "Siliniyor..." : "Sil"}</button>
            </div>
          </div>
        </div>
      )}

      {weightConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999,
        }}>
          <div style={{
            background: "var(--surface)", border: "1.5px solid var(--border)",
            borderRadius: 12, padding: "28px 28px 22px", maxWidth: 380, width: "90%",
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>
              <IcScale size={15} /> Gramaj Ayarını Değiştir
            </div>
            <div style={{ fontSize: 14, color: "var(--text2)", marginBottom: 18 }}>
              {editData.is_weight_based === 1
                ? <>Bu ürün <strong>gramaj bazlı fiyatlandırmadan çıkarılacak.</strong> Mevcut siparişler etkilenmez, yeni siparişlerde sabit fiyat uygulanır.</>
                : <>Bu ürün <strong>gramaj bazlı fiyatlandırmaya alınacak.</strong> Girilen fiyat 100g baz fiyatı olarak kullanılır. Mevcut siparişler etkilenmez.</>
              }
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setWeightConfirm(false)}
                style={{
                  background: "var(--surface2)", border: "1px solid var(--border)",
                  borderRadius: 8, color: "var(--text1)", padding: "9px 18px",
                  cursor: "pointer", fontSize: 14,
                }}
              >İptal</button>
              <button
                onClick={() => {
                  setEditData(d => ({...d, is_weight_based: d.is_weight_based === 1 ? 0 : 1}));
                  setWeightConfirm(false);
                }}
                style={{
                  background: "#388e3c", border: "none", borderRadius: 8,
                  color: "#fff", padding: "9px 18px", cursor: "pointer",
                  fontSize: 14, fontWeight: 600,
                }}
              >Evet, değiştir</button>
            </div>
          </div>
        </div>
      )}

      {/* Araç çubuğu — Görsel + Sıralama */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 10 }}>
        {/* Sıralamayı Düzenle */}
        <button
          onClick={() => setSortMode(m => !m)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "8px 18px", borderRadius: 50, cursor: "pointer",
            border: sortMode ? "none" : "1.5px solid var(--border)",
            background: sortMode ? "var(--accent,#5a9e72)" : "var(--surface2)",
            color: sortMode ? "#ffffff" : "var(--text2)",
            fontWeight: 600, fontSize: 13,
            boxShadow: sortMode ? "0 2px 12px rgba(90,158,114,0.35)" : "none",
            transition: "all 0.18s ease",
          }}
        >
          <span style={{ fontSize: 14 }}>↕</span>
          <span>{sortMode ? "Sıralamayı Bitir" : "Sıralamayı Düzenle"}</span>
        </button>

        {/* Görsel Modu */}
        <button
          onClick={() => setPhotoMode(m => !m)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "8px 18px", borderRadius: 50, cursor: "pointer",
            border: photoMode ? "none" : "1.5px solid var(--border)",
            background: photoMode ? "var(--sidebar, #1a1b3a)" : "var(--surface2)",
            color: photoMode ? "#ffffff" : "var(--text2)",
            fontWeight: 600, fontSize: 13,
            boxShadow: photoMode ? "0 2px 12px rgba(0,0,0,0.18)" : "none",
            transition: "all 0.18s ease",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center" }}><IcCamera size={15} /></span>
          <span>{photoMode ? "Görsel Modu Açık" : "Görsel Ekle"}</span>
          <span style={{
            width: 18, height: 18, borderRadius: "50%",
            background: photoMode ? "rgba(255,255,255,0.25)" : "var(--border)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 9, fontWeight: 700,
          }}>{photoMode ? "✕" : "+"}</span>
        </button>
      </div>

      <div className="pt-scroll">
      <div className="product-table">
        <div className="pt-header">
          <span>Ürün</span>
          <span>#</span>
          <span className="align-right">Fiyat</span>
          <span className="align-right">Maliyet</span>
          <span className="align-right">Kâr</span>
          <span className="align-right">Marj</span>
          <span>Durum</span>
          <span></span>
        </div>

        {categories.map(cat => {
          // Sıralama için TÜM ürünler (filtre uygulanmaz — indeks tutarlılığı için)
          const allCatProds = products
            .filter(p => p.category_id === cat.id)
            .slice()
            .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.id - b.id);

          // Görüntüleme için filtrelenmiş ürünler
          const visibleProds = allCatProds.filter(statusFilter);

          if (visibleProds.length === 0) return null;

          return (
            <React.Fragment key={cat.id}>
              {/* ── Kategori başlığı ── */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "9px 14px 7px", marginTop: 10,
                background: "var(--surface2)", borderRadius: "10px 10px 0 0",
                borderBottom: "2px solid var(--border)",
              }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)", display: "flex", alignItems: "center", gap: 7 }}>
                  {cat.emoji} {cat.name}
                </span>
                <span style={{ fontSize: 11, color: "var(--text3)" }}>{allCatProds.length} ürün</span>
              </div>

              {visibleProds.map((p, visIdx) => {
                const allIdx    = allCatProds.findIndex(x => x.id === p.id);
                const isFirst   = allIdx === 0;
                const isLast    = allIdx === allCatProds.length - 1;
                const isMoving  = movingId === p.id;
                const isLastVis = visIdx === visibleProds.length - 1;

                return (
                  <div
                    key={p.id}
                    className={`pt-row${editingId === p.id ? " pt-row--editing" : ""}${p.is_active === 0 ? " inactive" : ""}`}
                    style={{ borderRadius: isLastVis ? "0 0 10px 10px" : 0, borderBottom: isLastVis ? "none" : "1px solid var(--border)" }}
                  >
                    {editingId === p.id ? (
                      <>
                        <div className="edit-inline">
                          <div className="edit-inline-row">
                            <input value={editData.image_emoji} onChange={e => setEditData({...editData, image_emoji: e.target.value})} style={{width:48}} />
                            <input value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} style={{flex:1}} />
                            <button
                              onClick={() => setWeightConfirm(true)}
                              title="Gramaj bazlı fiyatlandırma"
                              style={{
                                padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                                cursor: "pointer", border: "none", whiteSpace: "nowrap", flexShrink: 0,
                                background: editData.is_weight_based === 1 ? "#e8f5e9" : "var(--surface3)",
                                color:      editData.is_weight_based === 1 ? "#388e3c" : "var(--text3)",
                                outline: editData.is_weight_based === 1 ? "1.5px solid #a5d6a7" : "1.5px solid var(--border)",
                              }}
                            >
                              <IcScale size={14} /> {editData.is_weight_based === 1 ? "Gramaj ✓" : "Gramaj"}
                            </button>
                            <button
                              onClick={() => setEditData(d => ({ ...d, bean_selectable: d.bean_selectable === 1 ? 0 : 1 }))}
                              title="Çekirdek seçimi"
                              style={{
                                padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                                cursor: "pointer", border: "none", whiteSpace: "nowrap", flexShrink: 0,
                                background: editData.bean_selectable === 1 ? "#fff8e1" : "var(--surface3)",
                                color:      editData.bean_selectable === 1 ? "#b8860b" : "var(--text3)",
                                outline: editData.bean_selectable === 1 ? "1.5px solid #ffe082" : "1.5px solid var(--border)",
                              }}
                            >
                              <IcCoffee size={14} /> {editData.bean_selectable === 1 ? "Çekirdek ✓" : "Çekirdek"}
                            </button>
                          </div>
                          <input
                            value={editData.description || ""}
                            onChange={e => setEditData({...editData, description: e.target.value})}
                            placeholder="Ürün açıklaması (opsiyonel)"
                            className="edit-desc-input"
                          />
                          <input
                            value={editData.taste_notes || ""}
                            onChange={e => setEditData({...editData, taste_notes: e.target.value})}
                            placeholder="Tat notları (virgülle: karamelize, vanilya)"
                            className="edit-desc-input"
                          />
                          <input
                            value={editData.allergens || ""}
                            onChange={e => setEditData({...editData, allergens: e.target.value})}
                            placeholder="Alerjen (opsiyonel: süt, yumurta)"
                            className="edit-desc-input"
                          />
                        </div>
                        <select
                          value={editData.category_id}
                          onChange={e => setEditData({...editData, category_id: Number(e.target.value)})}
                          style={{ fontSize:13, borderRadius:6, border:"1px solid var(--border)",
                                   background:"var(--surface)", color:"var(--text)", padding:"4px 6px" }}>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>
                          ))}
                        </select>
                        <input className="num-input" type="number" value={editData.price} onChange={e => setEditData({...editData, price: e.target.value})} />
                        <input className="num-input" type="number" value={editData.cost} onChange={e => setEditData({...editData, cost: e.target.value})} />
                        <span className="align-right profit">₺{Math.round(editData.price - editData.cost)}</span>
                        <span className="align-right margin">{editData.price > 0 ? Math.round((editData.price - editData.cost)/editData.price*100) : 0}%</span>
                        <span />
                        <div className="row-actions">
                          <button className="btn-save-sm" onClick={() => saveEdit(p.id)}>✓</button>
                          <button className="btn-cancel-sm" onClick={() => setEditingId(null)}>✕</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="product-name-cell" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          {/* ↑/↓ — sadece sort modu açıkken */}
                          {sortMode && (
                            <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
                              <span className="sort-num">{allIdx + 1}</span>
                              <div className={`sort-pill${isMoving ? " moving" : ""}`}>
                                <button
                                  className="sort-pill-btn"
                                  onClick={() => moveProduct(cat.id, allIdx, -1)}
                                  disabled={isFirst || movingId !== null}
                                  title="Yukarı taşı"
                                >
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1.5 7L5 3l3.5 4"/>
                                  </svg>
                                </button>
                                <button
                                  className="sort-pill-btn"
                                  onClick={() => moveProduct(cat.id, allIdx, +1)}
                                  disabled={isLast || movingId !== null}
                                  title="Aşağı taşı"
                                >
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1.5 3L5 7l3.5-4"/>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                          <span style={{ flexShrink: 0 }}>
                            {p.image_url && !brokenImages[p.id] ? (
                              <img
                                src={p.image_url}
                                alt={p.name}
                                onError={() => setBrokenImages(b => ({...b, [p.id]: true}))}
                                onClick={() => photoMode && window.open(p.image_url, "_blank")}
                                style={{
                                  width: photoMode ? 48 : 32, height: photoMode ? 48 : 32,
                                  borderRadius: 6, objectFit: "cover",
                                  border: "1px solid var(--border)", display: "block",
                                  cursor: photoMode ? "zoom-in" : "default",
                                }}
                              />
                            ) : (
                              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--text3)" }}>
                                <ProductGlyph emoji={p.image_emoji} size={photoMode ? 24 : 19} />
                              </span>
                            )}
                          </span>

                          <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                            <span style={{ fontWeight: 500, fontSize: 14, color: "var(--text)" }}>
                              {p.name}
                              {p.is_active === 0 && (
                                <span style={{
                                  marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                                  background: "#fdecea", color: "#c62828", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle",
                                }}>PASİF</span>
                              )}
                              {p.image_url && (
                                p.image_width && p.image_height && (p.image_width / p.image_height) >= 1.25 ? (
                                  <span title="Landscape — vitrine girebilir" style={{
                                    marginLeft: 6, fontSize: 10, fontWeight: 700,
                                    background: "#e8f5e9", color: "#2e7d32",
                                    borderRadius: 4, padding: "1px 5px", verticalAlign: "middle",
                                  }}>🖼 Vitrin ✓</span>
                                ) : (
                                  <span title="Kare/dikey görsel — vitrine giremiyor" style={{
                                    marginLeft: 6, fontSize: 10, fontWeight: 700,
                                    background: "#fff3e0", color: "#e65100",
                                    borderRadius: 4, padding: "1px 5px", verticalAlign: "middle",
                                  }}>🖼 Vitrin ✗</span>
                                )
                              )}
                            </span>
                            {photoMode && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                                <input
                                  type="file" accept="image/jpeg,image/png,image/webp"
                                  style={{ display: "none" }} id={`img-upload-${p.id}`}
                                  onChange={e => { const file = e.target.files?.[0]; if (file) uploadProductImage(p.id, file); e.target.value = ""; }}
                                />
                                <label htmlFor={`img-upload-${p.id}`} style={{
                                  display: "inline-flex", alignItems: "center",
                                  cursor: uploadingId === p.id ? "not-allowed" : "pointer",
                                  opacity: uploadingId === p.id ? 0.5 : 1,
                                  background: "var(--surface2)", color: "var(--text2)",
                                  border: "1px solid var(--border)", borderRadius: 8,
                                  padding: "4px 10px", minHeight: 36, fontSize: 12, fontWeight: 500,
                                  pointerEvents: uploadingId === p.id ? "none" : "auto",
                                }}>
                                  {uploadingId === p.id ? "Yükleniyor…" : <span style={{display:"inline-flex",alignItems:"center",gap:6}}><IcCamera size={13} /> Yükle</span>}
                                </label>
                                {p.image_url && (
                                  <button onClick={() => deleteProductImage(p.id)} disabled={uploadingId === p.id} style={{
                                    display: "inline-flex", alignItems: "center",
                                    background: "var(--surface2)", color: "var(--text3)",
                                    border: "1px solid var(--border)", borderRadius: 8,
                                    padding: "4px 10px", minHeight: 36, fontSize: 12,
                                    cursor: uploadingId === p.id ? "not-allowed" : "pointer",
                                  }}>Kaldır</button>
                                )}
                                {imageSuccess[p.id] && <div style={{ color: "#2e7d32", fontSize: 11, fontWeight: 600 }}>{imageSuccess[p.id]}</div>}
                                {imageErrors[p.id]   && <div style={{ color: "#c62828", fontSize: 11, fontWeight: 600 }}>{imageErrors[p.id]}</div>}
                              </div>
                            )}
                          </span>
                        </div>

                        {/* Sıra numarası */}
                        <span className="cat-tag" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {allIdx + 1}
                        </span>

                        <span className="align-right price-cell">
                          ₺{p.price}
                          {p.is_weight_based === 1 && (
                            <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, background: "#e8f5e9", color: "#388e3c", borderRadius: 4, padding: "1px 4px" }}>/100g</span>
                          )}
                        </span>
                        <span className="align-right cost-cell">₺{p.cost}</span>
                        <span className="align-right profit">₺{Math.round(p.price - p.cost)}</span>
                        <span className="align-right margin">{p.price > 0 ? Math.round((p.price - p.cost)/p.price*100) : 0}%</span>
                        <button
                          className={`status-toggle${p.is_active ? " active" : " passive"}`}
                          onClick={() => p.is_active ? setDeactivateConfirm(p) : toggleActive(p)}
                        >
                          {p.is_active ? "Aktif" : "Aktif Et"}
                        </button>

                        <div className="row-actions">
                          {/* Öne çıkar toggle */}
                          <button
                            className="pt-icon-btn"
                            title={p.is_featured ? "Slider'dan çıkar" : "Slider'a ekle"}
                            onClick={async () => {
                              const val = p.is_featured ? 0 : 1;
                              setProducts(ps => ps.map(x => x.id === p.id ? { ...x, is_featured: val } : x));
                              await fetch(`${API}/api/admin/products/${p.id}`, {
                                method: "PATCH", credentials: "include",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ is_featured: val }),
                              });
                            }}
                            style={{
                              border: p.is_featured ? "1.5px solid #f59e0b" : "1px solid var(--border)",
                              background: p.is_featured ? "rgba(245,158,11,0.15)" : "var(--surface2)",
                              color: p.is_featured ? "#f59e0b" : "var(--text3)",
                            }}
                          ><IcStar size={15} /></button>
                          <button className="pt-icon-btn" title="Düzenle" onClick={() => startEdit(p)}><IcEdit size={15} /></button>
                          {p.has_orders ? (
                            <span
                              className="pt-icon-btn pt-icon-locked"
                              title="Geçmiş siparişlerde kullanıldı — kalıcı silinemez"
                            ><IcLock size={14} /></span>
                          ) : (
                            <button className="pt-icon-btn pt-icon-danger" title="Kalıcı sil (geçmiş sipariş yok)"
                              onClick={() => { setDeleteConfirm(p); setDeleteError(""); }}
                            ><IcTrash size={15} /></button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      </div>
    </div>
  );
}

// ── Tables Tab ────────────────────────────────────────────────────────────────
function TablesTab() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newTable, setNewTable] = useState({ label: "", location: "ic" });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/admin/tables`, { credentials: 'include' });
    const json = await res.json();
    // Only show ACTIVE tables (is_active = 1)
    setTables((json.tables || []).filter(t => t.is_active === 1));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveNew = async () => {
    if (!newTable.label) return;
    const res = await fetch(`${API}/api/admin/tables`, {
      method: "POST",
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...newTable }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || "Masa eklenemedi");
      return;
    }
    setAdding(false);
    setNewTable({ label: "", location: "ic" });
    load();
  };

  const saveEdit = async (id) => {
    await fetch(`${API}/api/admin/tables/${id}`, {
      method: "PATCH",
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...editData }),
    });
    setEditingId(null);
    load();
  };

  const toggleActive = async (t) => {
    await fetch(`${API}/api/admin/tables/${t.id}`, {
      method: "PATCH",
      credentials: 'include',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_active: t.is_active === 1 ? 0 : 1 }),
    });
    load();
  };

  const deleteTable = async (t) => {
    if (!confirm(`${t.label} silinecek. Emin misiniz?`)) return;
    await fetch(`${API}/api/admin/tables/${t.id}`, { method: "DELETE", credentials: 'include' });
    load();
  };

  const [qrModal, setQrModal] = useState(null); // {qr_svg, qr_url, table_label}

  const openQr = async (t) => {
    try {
      const res = await fetch(`${API}/api/admin/tables/${t.id}/qr-data`, {
        credentials: 'include',
      });
      if (!res.ok) { alert("QR verisi alınamadı"); return; }
      const data = await res.json();
      setQrModal(data);
    } catch {
      alert("Bağlantı hatası");
    }
  };

  const refreshToken = async (t) => {
    if (!window.confirm(
      `"${t.label}" için QR token yenilenecek.\n\nEski QR kodlar artık geçersiz olacak — yeni QR kodları yazdırıp masalara yapıştırmanız gerekecek.\n\nDevam etmek istiyor musunuz?`
    )) return;
    const res = await fetch(`${API}/api/admin/tables/${t.id}/refresh-token`, {
      method: "POST",
      credentials: 'include',
    });
    if (res.ok) {
      alert(`Token yenilendi. "${t.label}" için yeni QR kodu yazdırın.`);
      load();
    } else {
      alert("Token yenilenemedi.");
    }
  };

  const ic = tables.filter(t => t.location === "ic");
  const dis = tables.filter(t => t.location === "dis");

  if (loading) return <div className="tab-loading">Yükleniyor…</div>;

  return (
    <div className="tab-content">
      {qrModal && (
        <div className="modal-overlay" onClick={() => setQrModal(null)}>
          <div className="qr-modal-card" onClick={e => e.stopPropagation()}>
            <button className="qr-modal-close" onClick={() => setQrModal(null)}>✕</button>
            <div className="qr-modal-brand">VESAiRE</div>
            <div className="qr-modal-table">{qrModal.table_label}</div>
            <div className="qr-modal-svg" dangerouslySetInnerHTML={{ __html: qrModal.qr_svg }} />
            <div className="qr-modal-cta">QR kodu okutun<br />menüyü görün, sipariş verin</div>
            <div className="qr-modal-url">{qrModal.qr_url}</div>
            <button className="qr-modal-print-btn" onClick={() => window.print()}>Yazdır / PDF Al</button>
          </div>
        </div>
      )}

      <SectionTitle action={
        <button className="add-btn" onClick={() => setAdding(true)}>+ Masa Ekle</button>
      }>
        Masalar
      </SectionTitle>

      {adding && (
        <div className="add-form">
          <div className="add-form-title">Yeni Masa</div>
          <div className="form-row">
            <input placeholder="Masa adı (ör: Masa 9 İç)" value={newTable.label} onChange={e => setNewTable({...newTable, label: e.target.value})} style={{flex:2}} />
            <select value={newTable.location} onChange={e => setNewTable({...newTable, location: e.target.value})}>
              <option value="ic">İç Mekan</option>
              <option value="dis">Dış Mekan</option>
            </select>
          </div>
          <div className="form-actions">
            <button className="btn-save" onClick={saveNew}>Kaydet</button>
            <button className="btn-cancel" onClick={() => setAdding(false)}>İptal</button>
          </div>
        </div>
      )}

      {[{ type: "ic", label: "İç Mekan", items: ic }, { type: "dis", label: "Dış Mekan", items: dis }].map(({ type, label, items }) => (
        <div key={label} className="table-group">
          <div className="table-group-title">
            <LocLabel type={type} size={16}>{label}</LocLabel>
            <span className="tg-count">{items.length} masa</span>
          </div>
          <div className="tables-grid">
            {items.map(t => (
              <div key={t.id} className={`table-card${t.is_active === 0 ? " inactive" : ""}`}>
                {editingId === t.id ? (
                  <div className="table-edit">
                    <input value={editData.label} onChange={e => setEditData({...editData, label: e.target.value})} />
                    <select value={editData.location} onChange={e => setEditData({...editData, location: e.target.value})}>
                      <option value="ic">İç</option>
                      <option value="dis">Dış</option>
                    </select>
                    <div className="form-actions">
                      <button className="btn-save-sm" onClick={() => saveEdit(t.id)}>✓</button>
                      <button className="btn-cancel-sm" onClick={() => setEditingId(null)}>✕</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tc-label">{t.label}</div>
                    <div className="tc-code">{t.code}</div>
                    <div className="tc-actions">
                      <button className="btn-edit" title="Düzenle" onClick={() => { setEditingId(t.id); setEditData({ label: t.label, location: t.location }); }}><IcEdit size={15} /></button>
                      <button className="btn-edit" title="Sipariş QR Kodunu Görüntüle / Yazdır" onClick={() => openQr(t)}><IcQr size={15} /></button>
                      <button className="btn-cancel-sm" title="QR Token Yenile (eski QR kodlar geçersiz olur)" onClick={() => refreshToken(t)}><IcRefresh size={15} /></button>
                      <button className="btn-delete" title="Sil" onClick={() => deleteTable(t)}><IcTrash size={15} /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab() {
  const { user } = useAuth();
  const [reportTime, setReportTime] = useState("22:00");
  const [reportEmail, setReportEmail] = useState("");
  const [reportEnabled, setReportEnabled] = useState(false);
  const [monthlyReportEnabled, setMonthlyReportEnabled] = useState(false);
  const [reportMsg, setReportMsg] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendReportModal, setSendReportModal] = useState(false);
  const [sendReportMsg, setSendReportMsg] = useState(null);
  const [sendingMonthlyReport, setSendingMonthlyReport] = useState(false);
  const [sendMonthlyReportMsg, setSendMonthlyReportMsg] = useState(null);
  const [ownerTokenModal, setOwnerTokenModal] = useState(false);
  const [ownerTokenInput, setOwnerTokenInput] = useState("");


  const [orderingEnabled, setOrderingEnabled] = useState(true);
  const [orderingMsg, setOrderingMsg] = useState(null);
  const [orderingConfirm, setOrderingConfirm] = useState(null); // {val: bool}
  const [menuStyle, setMenuStyle]   = useState("classic"); // classic | premium
  const [menuStyleMsg, setMenuStyleMsg] = useState(null);
  const [menuStyleConfirm, setMenuStyleConfirm] = useState(null); // bekleyen görünüm ("classic"|"premium")
  const [heroImage, setHeroImage]   = useState("");
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroMsg, setHeroMsg]       = useState(null);

  // Tema
  const [activeTheme, setActiveTheme] = useState(
    () => localStorage.getItem("coas_theme") || "vesaire"
  );

  const THEMES = [
    { id: "likapai",       label: "LikapAI",       bg: "#F4F5FC", accent: "#5B5EA6", sidebar: "#1A1B3A" },
    { id: "vesaire",       label: "Sage",          bg: "#F2F5F0", accent: "#4E7C62", sidebar: "#1E2B20" },
    { id: "mocha",         label: "Mocha",         bg: "#F9F4EE", accent: "#5C2E10", sidebar: "#1C0C06" },
    { id: "patisserie",    label: "Patisserie",    bg: "#FBF8F6", accent: "#B06050", sidebar: "#342028" },
    { id: "cocoa",         label: "Mist Blue",     bg: "#F4F8FB", accent: "#4EA5D9", sidebar: "#1E2A38" },
    { id: "premium-dark",  label: "Premium Dark",  bg: "#0a0a12", accent: "#10b981", sidebar: "#08080f" },
  ];

  const applyTheme = (id) => {
    localStorage.setItem("coas_theme", id);
    setActiveTheme(id);
    window.dispatchEvent(new CustomEvent("coas-theme-changed", { detail: { theme: id } }));
    // Backend'e kaydet — diğer tarayıcılar / cihazlar da aynı temayı görsün
    fetch(`${API}/api/admin/settings/theme?theme=${encodeURIComponent(id)}`, {
      method: "PUT",
      credentials: "include",
    }).catch(() => {});
  };

  // Alarm ayarları
  const [alertSound, setAlertSound] = useState("classic_ding");
  const [alertMode,  setAlertMode]  = useState("once");
  const [alertMsg,   setAlertMsg]   = useState(null);

  // Şifre değiştirme
  const [curPw,      setCurPw]      = useState("");
  const [newPw,      setNewPw]      = useState("");
  const [newPw2,     setNewPw2]     = useState("");
  const [pwMsg,      setPwMsg]      = useState(null);
  const [pwBusy,     setPwBusy]     = useState(false);
  const [pwConfirm,  setPwConfirm]  = useState(false);

  // LikapAI abonelik durumu
  const [subscription, setSubscription] = useState(null);

  const loadSubscription = async () => {
    try {
      const res = await fetch(`${API}/api/admin/settings/subscription`, { credentials: "include" });
      setSubscription(await res.json());
    } catch { setSubscription({ available: false, reason: "unreachable" }); }
  };

  // Çekirdek seçenekleri
  const [beanOptions,  setBeanOptions]  = useState([]);
  const [beanMsg,      setBeanMsg]      = useState(null);
  const [beanBusy,     setBeanBusy]     = useState(false);
  const [beanEditMode, setBeanEditMode] = useState(false);
  const [beanDraft,    setBeanDraft]    = useState([]);

  const loadBeanOptions = async () => {
    try {
      const res = await fetch(`${API}/api/admin/settings/bean-options`, { credentials: "include" });
      const d = await res.json();
      setBeanOptions(d.options || [{ name: "Barista Seçimi", desc: "" }]);
    } catch { setBeanOptions([{ name: "Barista Seçimi", desc: "" }]); }
  };

  const saveBeanOptions = async () => {
    setBeanBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/settings/bean-options`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ options: beanOptions }),
      });
      if (!res.ok) throw new Error();
      setBeanMsg({ err: false, text: "Kaydedildi ✓" });
      setBeanEditMode(false);
    } catch {
      setBeanMsg({ err: true, text: "Kayıt başarısız" });
    } finally {
      setBeanBusy(false);
      setTimeout(() => setBeanMsg(null), 3000);
    }
  };

  useEffect(() => {
    loadReportSettings();
    loadOrderingEnabled();
    loadMenuStyle();
    loadHeroImage();
    loadAlertSettings();
    loadBeanOptions();
    loadSubscription();
  }, []);
  
  const loadAlertSettings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/alert-settings`, { credentials: 'include' });
      const d = await res.json();
      setAlertSound(d.order_alert_sound || "classic_ding");
      setAlertMode(d.order_alert_mode   || "once");
    } catch (e) {
      console.error("Alert settings load failed:", e);
    }
  };

  const saveAlertSettings = async () => {
    try {
      await fetch(`${API}/api/admin/alert-settings`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_alert_sound: alertSound, order_alert_mode: alertMode }),
      });
      // App.js'deki alertSettingsRef'i anında güncelle — sayfa yenilemesi gerekmez
      window.dispatchEvent(new CustomEvent("alert-settings-updated", {
        detail: { order_alert_sound: alertSound, order_alert_mode: alertMode },
      }));
      setAlertMsg({ err: false, text: "Alarm ayarları kaydedildi ✓" });
    } catch {
      setAlertMsg({ err: true, text: "Kaydetme başarısız" });
    }
    setTimeout(() => setAlertMsg(null), 3000);
  };

  const loadReportSettings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/report-settings`, { credentials: 'include' });
      const data = await res.json();
      setReportTime(data.report_time || "22:00");
      setReportEmail(data.report_email || "");
      setReportEnabled(data.report_enabled || false);
      setMonthlyReportEnabled(data.monthly_report_enabled || false);
    } catch (e) {
      console.error("Report settings load failed:", e);
    }
  };
  
  
  const saveReportSettings = async () => {
    try {
      await fetch(`${API}/api/admin/report-settings`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ time: reportTime, email: reportEmail, enabled: reportEnabled, monthly_report_enabled: monthlyReportEnabled })
      });
      setReportMsg({ err: false, text: "Rapor ayarları kaydedildi ✓" });
    } catch {
      setReportMsg({ err: true, text: "Kaydetme başarısız" });
    }
  };
  

  const loadOrderingEnabled = async () => {
    try {
      const res = await fetch(`${API}/api/admin/settings/ordering-enabled`);
      const data = await res.json();
      setOrderingEnabled(data.enabled !== false);
    } catch (e) { console.error(e); }
  };

  const loadHeroImage = async () => {
    try {
      const res = await fetch(`${API}/api/admin/settings/theme`);
      const data = await res.json();
      setHeroImage(data.hero_image || "");
    } catch (e) { console.error(e); }
  };

  const uploadHero = async (fileList) => {
    const f = fileList && fileList[0];
    if (!f) return;
    setHeroUploading(true); setHeroMsg(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`${API}/api/admin/settings/hero-image`, { method: "POST", credentials: "include", body: fd });
      const data = await res.json();
      if (res.ok) { setHeroImage(data.hero_image); setHeroMsg({ ok: true, text: "Hero görseli yüklendi ✓" }); }
      else setHeroMsg({ err: true, text: data.detail || "Yükleme başarısız" });
    } catch { setHeroMsg({ err: true, text: "Bağlantı hatası" }); }
    setHeroUploading(false);
    setTimeout(() => setHeroMsg(null), 3000);
  };

  const removeHero = async () => {
    try { await fetch(`${API}/api/admin/settings/hero-image`, { method: "DELETE", credentials: "include" }); setHeroImage(""); } catch {}
  };

  const loadMenuStyle = async () => {
    try {
      const res = await fetch(`${API}/api/admin/settings/menu-style`);
      const data = await res.json();
      setMenuStyle(data.style === "premium" ? "premium" : "classic");
    } catch (e) { console.error(e); }
  };

  const saveMenuStyle = async (style) => {
    const prev = menuStyle;
    setMenuStyle(style);
    try {
      const res = await fetch(`${API}/api/admin/settings/menu-style?style=${style}`, {
        method: "PUT",
        credentials: 'include',
      });
      if (res.ok) setMenuStyleMsg({ err: false, text: style === "premium" ? "Premium menü seçildi ✓" : "Klasik menü seçildi ✓" });
      else { setMenuStyle(prev); setMenuStyleMsg({ err: true, text: "Kaydetme başarısız" }); }
    } catch {
      setMenuStyle(prev); setMenuStyleMsg({ err: true, text: "Bağlantı hatası" });
    }
    setTimeout(() => setMenuStyleMsg(null), 3000);
  };

  const saveOrderingEnabled = async (val) => {
    try {
      const res = await fetch(`${API}/api/admin/settings/ordering-enabled?enabled=${val}`, {
        method: "PUT",
        credentials: 'include',
      });
      if (res.ok) setOrderingMsg({ err: false, text: val ? "Sipariş alma açıldı ✓" : "Sipariş alma kapatıldı ✓" });
      else setOrderingMsg({ err: true, text: "Kaydetme başarısız" });
    } catch {
      setOrderingMsg({ err: true, text: "Bağlantı hatası" });
    }
    setTimeout(() => setOrderingMsg(null), 3000);
  };

  const sendReportNow = async () => {
    setSendReportModal(false);
    setSendingReport(true);
    setSendReportMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/send-report-now`, {
        method: "POST",
        credentials: 'include',
      });
      const data = await res.json();
      setSendReportMsg(res.ok
        ? { err: false, text: "Rapor gönderildi ✓" }
        : { err: true, text: data.detail || "Gönderilemedi" }
      );
    } catch {
      setSendReportMsg({ err: true, text: "Bağlantı hatası" });
    }
    setSendingReport(false);
  };

  const sendMonthlyReportNow = async (token) => {
    setSendingMonthlyReport(true);
    setSendMonthlyReportMsg(null);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["X-Owner-Token"] = token;
      const res = await fetch(`${API}/api/admin/send-monthly-report-now`, {
        method: "POST",
        credentials: 'include',
        headers,
      });
      const data = await res.json();
      const msg = res.ok
        ? { err: false, text: "Aylık rapor gönderildi ✓" }
        : { err: true, text: data.detail || "Gönderilemedi" };
      setSendMonthlyReportMsg(msg);
      if (res.ok) setTimeout(() => { setOwnerTokenModal(false); setSendMonthlyReportMsg(null); }, 2000);
    } catch {
      setSendMonthlyReportMsg({ err: true, text: "Bağlantı hatası" });
    }
    setSendingMonthlyReport(false);
    setOwnerTokenInput("");
  };

  return (
    <div className="tab-content">
      <SectionTitle>Ayarlar</SectionTitle>

      {/* ── LikapAI Abonelik ── */}
      {subscription?.available && (() => {
        const d = subscription.days_left;
        const expired  = d != null && d < 0;
        const critical = d != null && d >= 0 && d <= 7;
        const warning  = d != null && d > 7 && d <= 30;
        const color  = expired || critical ? "#e57373" : warning ? "#f5a623" : "var(--mint)";
        const PLAN_LBL   = { trial: "Deneme", standard: "Standart", premium: "Premium", custom: "Özel" };
        const PERIOD_LBL = { monthly: "Aylık", quarterly: "3 Aylık", yearly: "Yıllık", biennial: "2 Yıllık", one_time: "Tek seferlik" };
        const fmt = (s) => s
          ? new Date(s + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
          : null;
        const startStr = fmt(subscription.plan_start);
        const endStr   = fmt(subscription.plan_end);
        let pct = null;
        if (subscription.plan_start && subscription.plan_end) {
          const s = new Date(subscription.plan_start + "T00:00:00").getTime();
          const e = new Date(subscription.plan_end   + "T00:00:00").getTime();
          if (e > s) pct = Math.max(2, Math.min(100, ((Date.now() - s) / (e - s)) * 100));
        }
        return (
          <div className="settings-card sub-card" style={{ borderLeft: `3px solid ${color}` }}>
            <div className="sub-head">
              <div className="sub-titles">
                <span className="sub-cafe">{localStorage.getItem("coas_cafe_name") || process.env.REACT_APP_CAFE_NAME || ""}</span>
                <span className="sub-title">LikapAI Aboneliği</span>
              </div>
              <span className="sub-badge">
                {PLAN_LBL[subscription.plan] || subscription.plan}
                <span className="sub-badge-dot">·</span>
                {PERIOD_LBL[subscription.period] || subscription.period}
              </span>
            </div>

            {d != null ? (
              <div className="sub-remaining" style={{ color }}>
                {expired
                  ? `${-d} gün önce bitti`
                  : <><span className="sub-remaining-num">{d}</span> gün kaldı</>}
              </div>
            ) : (
              <div className="sub-remaining sub-remaining--muted">Bitiş tarihi belirtilmemiş</div>
            )}

            {pct != null && (
              <div className="sub-bar">
                <div className="sub-bar-fill" style={{ width: `${pct}%`, background: color }} />
              </div>
            )}

            {(startStr || endStr) && (
              <div className="sub-dates">
                {startStr && <span>Başladı <b>{startStr}</b></span>}
                {endStr   && <span>Bitiş <b>{endStr}</b></span>}
              </div>
            )}

            {(expired || critical) && (
              <div className="sub-warn">
                {expired
                  ? "Aboneliğiniz sona erdi — yenilemek için LikapAI ile iletişime geçin."
                  : "Aboneliğiniz yakında bitiyor — kesinti yaşamamak için yenileyin."}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Planlar / Fiyatlandırma ── */}
      {(() => {
        const PLANS = [
          {
            ad: "CafeOS",
            etiket: "Standart",
            aylik: "1.790",
            yillik: "17.900",
            yillikNot: "2 ay bedava",
            vurgu: false,
            kapsam: "QR menü + sipariş · barista/mutfak paneli · ödeme & adisyon · finans & kâr-zarar · Z raporu OCR · AI asistan · çoklu şube",
          },
          {
            ad: "Kurucu İşletme",
            etiket: "İlk 20 işletmeye özel",
            aylik: "990",
            yillik: "9.900",
            yillikNot: "12 ay fiyat sabit",
            vurgu: true,
            kapsam: "CafeOS'un tüm özellikleri — sınırlı kontenjan, erken katılım fiyatı",
          },
        ];
        return (
          <div className="settings-card">
            <div className="settings-card-title"><IcMoney size={17} /> Planlar</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 4 }}>
              {PLANS.map(p => (
                <div key={p.ad} style={{
                  border: `1px solid ${p.vurgu ? "rgba(var(--mint-rgb),0.5)" : "var(--border)"}`,
                  borderRadius: 12, padding: "16px 18px",
                  background: p.vurgu ? "rgba(var(--mint-rgb),0.06)" : "var(--surface2)",
                  position: "relative",
                }}>
                  {p.vurgu && (
                    <span style={{
                      position: "absolute", top: -9, right: 14, fontSize: 10, fontWeight: 700,
                      background: "var(--mint)", color: "#fff", padding: "2px 8px", borderRadius: 6,
                      letterSpacing: "0.3px",
                    }}>SINIRLI</span>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 800, color: "var(--text)" }}>{p.ad}</div>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 12 }}>{p.etiket}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 24, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.5px" }}>₺{p.aylik}</span>
                    <span style={{ fontSize: 12, color: "var(--text3)" }}>+ KDV / ay</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 12 }}>
                    Yıllık <b>₺{p.yillik}</b> + KDV
                    <span style={{ color: "var(--mint-dark)", marginLeft: 6, fontWeight: 600 }}>· {p.yillikNot}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.6 }}>{p.kapsam}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 12, lineHeight: 1.6 }}>
              Yenileme veya plan değişikliği için LikapAI ile iletişime geçin. Kurucu İşletme fiyatı yalnızca ilk 20 işletme için geçerlidir ve kontenjan dolunca kapanır.
            </div>
          </div>
        );
      })()}

      {/* ── Tema ── */}
      <div className="settings-card">
        <div className="settings-card-title"><IcPalette size={17} /> Tema</div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "4px" }}>
          {THEMES.map(t => (
            <button
              key={t.id}
              onClick={() => applyTheme(t.id)}
              title={t.label}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: "6px", padding: "10px 14px", cursor: "pointer",
                border: activeTheme === t.id
                  ? `2px solid ${t.accent}`
                  : "2px solid transparent",
                borderRadius: "10px",
                background: activeTheme === t.id ? `${t.bg}` : "var(--surface2)",
                boxShadow: activeTheme === t.id ? `0 0 0 1px ${t.accent}` : "none",
                transition: "all 0.15s",
              }}
            >
              {/* Mini app preview */}
              <div style={{
                width: 56, height: 38, borderRadius: "6px", overflow: "hidden",
                display: "flex", border: "1px solid rgba(0,0,0,0.08)",
              }}>
                <div style={{ width: 14, background: t.sidebar, borderRadius: "5px 0 0 5px" }} />
                <div style={{ flex: 1, background: t.bg, display: "flex", flexDirection: "column",
                              padding: "3px", gap: "2px" }}>
                  <div style={{ height: 5, background: t.accent, borderRadius: 2, opacity: 0.9 }} />
                  <div style={{ height: 3, background: t.accent, borderRadius: 2, opacity: 0.4, width: "70%" }} />
                  <div style={{ height: 3, background: t.accent, borderRadius: 2, opacity: 0.4, width: "50%" }} />
                </div>
              </div>
              <span style={{
                fontSize: "11px", fontWeight: activeTheme === t.id ? 700 : 400,
                color: activeTheme === t.id ? t.accent : "var(--text3)",
              }}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Alarm Ayarları ── */}
      <div className="settings-card">
        <div className="settings-card-title"><IcBell size={17} /> Sipariş Alarm Ayarları</div>
        <div className="settings-form">
          <div className="input-group-v">
            <label>Alarm Sesi</label>
            <select value={alertSound} onChange={e => setAlertSound(e.target.value)}>
              <option value="classic_ding">Classic Ding</option>
              <option value="soft_bell">Soft Bell</option>
              <option value="digital_pop">Digital Pop</option>
              <option value="kitchen_ping">Kitchen Ping</option>
              <option value="double_beep">Double Beep</option>
            </select>
          </div>
          <div className="input-group-v">
            <label>Alarm Modu</label>
            <select value={alertMode} onChange={e => setAlertMode(e.target.value)}>
              <option value="once">Bir kez çal</option>
              <option value="repeat">Tekrar et (alarmı kapatana kadar)</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn-save" onClick={saveAlertSettings}>Kaydet</button>
            <button
              onClick={() => { import("./utils").then(u => u.playAlarmSound(alertSound)); }}
              style={{ padding: "10px 18px", borderRadius: "8px", border: "1px solid var(--border)",
                       background: "var(--bg)", cursor: "pointer", fontSize: "13px" }}
            >
              <IcVolume size={15} /> Test Et
            </button>
          </div>
          {alertMsg && <div className={`settings-msg${alertMsg.err ? " err" : " ok"}`}>{alertMsg.text}</div>}
        </div>
      </div>


      {/* ── Çekirdek Seçenekleri ── */}
      <div className="settings-card">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div className="settings-card-title" style={{ marginBottom:0 }}><IcCoffee size={17} /> Çekirdek Seçenekleri</div>
          {!beanEditMode && (
            <button
              onClick={() => { setBeanDraft(beanOptions.map(o => ({ ...o }))); setBeanEditMode(true); }}
              style={{ padding:"5px 14px", borderRadius:6, border:"1px solid var(--border)",
                       background:"var(--surface2)", color:"var(--text2)", cursor:"pointer", fontSize:12 }}
            >
              ✎ Düzenle
            </button>
          )}
        </div>
        <div className="settings-form">
          <p style={{ fontSize:12, color:"var(--text3)", marginBottom:8 }}>
            Müşteri menüsünde çekirdek seçimi açık olan ürünlerde gösterilecek seçenekler.
            İlk seçenek varsayılan olarak seçili gelir.
          </p>

          {!beanEditMode ? (
            /* ── Salt okunur görünüm ── */
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {beanOptions.map((opt, i) => (
                <div key={i} style={{ padding:"10px 12px", background:"var(--surface2)",
                                      borderRadius:10, border:"1px solid var(--border)" }}>
                  <div style={{ fontWeight:600, fontSize:13, color:"var(--text)", display:"flex", alignItems:"center", gap:6 }}>
                    {i === 0 && (
                      <span style={{ fontSize:10, color:"var(--accent,#5a9e72)", background:"var(--surface3)",
                                     padding:"1px 5px", borderRadius:4, flexShrink:0 }}>
                        Varsayılan
                      </span>
                    )}
                    {opt.name || <em style={{ color:"var(--text3)" }}>İsimsiz</em>}
                    {opt.story && (
                      <span style={{ fontSize:10, color:"var(--text3)", background:"var(--surface3)",
                                     padding:"1px 6px", borderRadius:4 }}>künye ✓</span>
                    )}
                  </div>
                  {(opt.line || opt.notes || opt.desc) && (
                    <div style={{ fontSize:11, color:"var(--text3)", marginTop:3 }}>
                      {opt.line || opt.notes || opt.desc}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* ── Düzenleme görünümü ── */
            <>
              {beanOptions.map((opt, i) => {
                const upd = (patch) => setBeanOptions(opts => opts.map((o, j) => j === i ? { ...o, ...patch } : o));
                const roastSet = opt.roast != null;
                const roastPct = Math.round((roastSet ? opt.roast : 0.5) * 100);
                const roastLbl = !roastSet ? "belirtilmedi" : (roastPct < 34 ? "açık" : (roastPct < 67 ? "orta" : "koyu"));
                const meta = Array.isArray(opt.meta) ? opt.meta : [];
                const fld = { fontSize:12, padding:"6px 8px", borderRadius:6, border:"1px solid var(--border)",
                              background:"var(--surface)", color:"var(--text)", minWidth:0, boxSizing:"border-box" };
                return (
                <div key={i} style={{ marginBottom:12, padding:"12px", background:"var(--surface2)",
                                      borderRadius:10, border:"1px solid var(--border)" }}>
                  <div style={{ display:"flex", gap:8, marginBottom:6 }}>
                    <input
                      value={opt.name}
                      onChange={e => upd({ name: e.target.value })}
                      placeholder="Çekirdek adı (ör. Dark Motion)"
                      style={{ ...fld, flex:1, fontSize:13, fontWeight:600 }}
                    />
                    <button
                      onClick={() => setBeanOptions(opts => opts.filter((_, j) => j !== i))}
                      disabled={beanOptions.length <= 1}
                      style={{ minWidth:32, minHeight:32, borderRadius:6, border:"1px solid var(--border)",
                               background:"var(--surface)", color:"var(--text2)", cursor:"pointer", fontSize:14 }}>
                      ✕
                    </button>
                  </div>

                  <input
                    value={opt.line || ""}
                    onChange={e => upd({ line: e.target.value })}
                    placeholder="Kısa tanım (ör. koyu kavurma · yoğun gövde)"
                    style={{ ...fld, width:"100%", marginBottom:6 }}
                  />

                  {/* Kavurma seviyesi — girilmemişse menüde kavurma çubuğu ÇIKMAZ.
                      Sürgüye dokununca kaydedilir; "kaldır" ile tekrar boşaltılır. */}
                  <div style={{ display:"flex", alignItems:"center", gap:10, margin:"8px 0 6px" }}>
                    <span style={{ fontSize:11, color:"var(--text3)", minWidth:60 }}>Kavurma</span>
                    <input
                      type="range" min="0" max="100" step="1" value={roastPct}
                      onChange={e => upd({ roast: Number(e.target.value) / 100, roastLabel: undefined })}
                      style={{ flex:1, accentColor: roastSet ? "#7fc9a6" : "var(--border)", opacity: roastSet ? 1 : 0.55 }}
                    />
                    <span style={{ fontSize:11, color: roastSet ? "var(--text2)" : "var(--text3)", minWidth:82, textAlign:"right", fontStyle: roastSet ? "normal" : "italic", whiteSpace:"nowrap" }}>{roastSet ? `${roastLbl} · %${roastPct}` : roastLbl}</span>
                    {roastSet && (
                      <button
                        onClick={() => upd({ roast: undefined, roastLabel: undefined })}
                        title="Kavurmayı kaldır"
                        style={{ border:"none", background:"none", color:"var(--text3)", cursor:"pointer", fontSize:12, padding:"0 2px" }}>
                        kaldır
                      </button>
                    )}
                  </div>

                  <input
                    value={opt.notes || ""}
                    onChange={e => upd({ notes: e.target.value })}
                    placeholder="Tat notları: bitter çikolata • fındık • melas"
                    style={{ ...fld, width:"100%", marginBottom:6 }}
                  />

                  <textarea
                    value={opt.story || ""}
                    onChange={e => upd({ story: e.target.value })}
                    placeholder="Hikâye (bu çekirdek neye yakışır, nasıl bir karakteri var…)"
                    rows={2}
                    style={{ ...fld, width:"100%", resize:"vertical", marginBottom:6, fontFamily:"inherit", lineHeight:1.5 }}
                  />

                  {/* Meta satırları (önerilen demleme, asidite …) */}
                  <div style={{ fontSize:11, color:"var(--text3)", margin:"2px 0 4px" }}>Künye satırları</div>
                  {meta.map((m, mi) => (
                    <div key={mi} style={{ display:"flex", gap:6, marginBottom:5 }}>
                      <input
                        value={m.k || ""}
                        onChange={e => upd({ meta: meta.map((x, k) => k === mi ? { ...x, k: e.target.value } : x) })}
                        placeholder="etiket (ör. önerilen demleme)"
                        style={{ ...fld, flex:1 }}
                      />
                      <input
                        value={m.v || ""}
                        onChange={e => upd({ meta: meta.map((x, k) => k === mi ? { ...x, v: e.target.value } : x) })}
                        placeholder="değer (ör. espresso · moka pot)"
                        style={{ ...fld, flex:1 }}
                      />
                      <button
                        onClick={() => upd({ meta: meta.filter((_, k) => k !== mi) })}
                        style={{ minWidth:28, borderRadius:6, border:"1px solid var(--border)",
                                 background:"var(--surface)", color:"var(--text3)", cursor:"pointer", fontSize:13 }}>
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => upd({ meta: [...meta, { k: "", v: "" }] })}
                    style={{ padding:"4px 10px", borderRadius:6, border:"1px dashed var(--border)",
                             background:"transparent", color:"var(--text3)", cursor:"pointer", fontSize:11 }}>
                    + Satır
                  </button>
                </div>
                );
              })}
              <button
                onClick={() => setBeanOptions(opts => [...opts, { name: "", line: "", notes: "", story: "", roast: 0.5, meta: [] }])}
                style={{ width:"100%", padding:"7px", borderRadius:8, border:"1.5px dashed var(--border)",
                         background:"transparent", color:"var(--text2)", cursor:"pointer", fontSize:13, marginTop:4 }}>
                + Seçenek Ekle
              </button>
              {beanMsg && <div className={`settings-msg${beanMsg.err ? " err" : " ok"}`}>{beanMsg.text}</div>}
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn-save" onClick={saveBeanOptions} disabled={beanBusy} style={{ flex:1 }}>
                  {beanBusy ? "Kaydediliyor…" : "Kaydet"}
                </button>
                <button
                  onClick={() => { setBeanOptions(beanDraft); setBeanEditMode(false); setBeanMsg(null); }}
                  disabled={beanBusy}
                  style={{ flex:1, padding:"10px", borderRadius:8, border:"1px solid var(--border)",
                           background:"var(--surface2)", color:"var(--text2)", cursor:"pointer", fontSize:13 }}>
                  İptal
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card-title"><IcChart size={17} /> Rapor Ayarları</div>
        <div className="settings-form">
          <div className="input-group-v">
            <label>E-posta Adresi</label>
            <input 
              type="email" 
              placeholder="ornek@gmail.com" 
              value={reportEmail} 
              onChange={e => setReportEmail(e.target.value)} 
            />
          </div>
          <div className="input-group-v">
            <label>Günlük Rapor Saati</label>
            <input 
              type="time" 
              value={reportTime} 
              onChange={e => setReportTime(e.target.value)} 
            />
          </div>
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="report-enabled"
              checked={reportEnabled}
              onChange={e => setReportEnabled(e.target.checked)}
            />
            <label htmlFor="report-enabled">Günlük rapor gönder</label>
          </div>
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="monthly-report-enabled"
              checked={monthlyReportEnabled}
              onChange={e => setMonthlyReportEnabled(e.target.checked)}
            />
            <label htmlFor="monthly-report-enabled">Aylık rapor gönder <span style={{fontSize:"11px",color:"var(--text3)"}}>Her ayın 1'i 10:00</span></label>
          </div>
          {reportMsg && <div className={`settings-msg${reportMsg.err ? " err" : " ok"}`}>{reportMsg.text}</div>}
          <div className="settings-btn-row">
            <button className="btn-save" onClick={saveReportSettings}>Rapor Ayarlarını Kaydet</button>
            <button className="btn-secondary" onClick={() => setSendReportModal(true)} disabled={sendingReport}>
              {sendingReport ? "Gönderiliyor…" : <><IcMail size={15} /> Şimdi Gönder</>}
            </button>
            <button className="btn-secondary" onClick={() => { setOwnerTokenInput(""); setOwnerTokenModal(true); }} disabled={sendingMonthlyReport}>
              {sendingMonthlyReport ? "Gönderiliyor…" : <><IcChart size={15} /> Aylık Rapor Gönder</>}
            </button>
          </div>
          {sendReportMsg && <div className={`settings-msg${sendReportMsg.err ? " err" : " ok"}`}>{sendReportMsg.text}</div>}
          {sendMonthlyReportMsg && <div className={`settings-msg${sendMonthlyReportMsg.err ? " err" : " ok"}`}>{sendMonthlyReportMsg.text}</div>}
        </div>
      </div>

      {/* ── Günlük Rapor Onay Modal ── */}
      {sendReportModal && (
        <div style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,
          backdropFilter:"blur(4px)"
        }}>
          <div style={{
            background:"var(--card,#fff)",borderRadius:20,padding:"32px 28px 24px",
            maxWidth:380,width:"92%",boxShadow:"0 12px 40px rgba(0,0,0,0.25)",
            border:"1px solid var(--border)"
          }}>
            <div style={{fontSize:17,fontWeight:700,color:"var(--text)",marginBottom:8}}><IcMail size={17} /> Günlük Rapor</div>
            <div style={{fontSize:14,color:"var(--text2)",marginBottom:24,lineHeight:1.6}}>
              Bugünün günlük raporu şimdi gönderilecek. Devam edilsin mi?
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button className="btn-secondary" onClick={() => setSendReportModal(false)}>İptal</button>
              <button className="btn-save" onClick={sendReportNow}>Gönder</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Aylık Rapor Token Modal ── */}
      {ownerTokenModal && (
        <div style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,
          backdropFilter:"blur(4px)"
        }}>
          <div style={{
            background:"var(--card,#fff)",borderRadius:20,padding:"32px 28px 24px",
            maxWidth:380,width:"92%",boxShadow:"0 12px 40px rgba(0,0,0,0.25)",
            border:"1px solid var(--border)"
          }}>
            <div style={{fontSize:17,fontWeight:700,color:"var(--text)",marginBottom:8}}><IcChart size={17} /> Aylık Rapor</div>
            {sendMonthlyReportMsg ? (
              <div style={{
                padding:"14px 16px",borderRadius:10,marginBottom:20,fontSize:14,
                background: sendMonthlyReportMsg.err ? "#fff0f0" : "#f0faf4",
                color: sendMonthlyReportMsg.err ? "#c0392b" : "#2e7d32",
                border: `1px solid ${sendMonthlyReportMsg.err ? "#f5c6cb" : "#b2dfdb"}`
              }}>
                {sendMonthlyReportMsg.text}
              </div>
            ) : (
              <>
                <div style={{fontSize:14,color:"var(--text2)",marginBottom:20,lineHeight:1.6}}>
                  {sendingMonthlyReport ? "Rapor gönderiliyor, lütfen bekleyin…" : "Geçen ayın aylık raporu gönderilecek. Devam etmek için şifrenizi girin."}
                </div>
                {!sendingMonthlyReport && (
                  <input
                    type="password"
                    autoFocus
                    value={ownerTokenInput}
                    onChange={e => setOwnerTokenInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && ownerTokenInput && sendMonthlyReportNow(ownerTokenInput)}
                    placeholder="Şifre..."
                    style={{
                      width:"100%",boxSizing:"border-box",padding:"12px 14px",
                      borderRadius:10,border:"1.5px solid var(--border)",
                      fontSize:15,background:"var(--surface2)",color:"var(--text)",
                      outline:"none",marginBottom:20
                    }}
                  />
                )}
              </>
            )}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button className="btn-secondary" onClick={() => { setOwnerTokenModal(false); setSendMonthlyReportMsg(null); }} disabled={sendingMonthlyReport}>
                {sendMonthlyReportMsg && !sendMonthlyReportMsg.err ? "Kapat" : "İptal"}
              </button>
              {!sendMonthlyReportMsg && (
                <button
                  className="btn-save"
                  disabled={!ownerTokenInput || sendingMonthlyReport}
                  onClick={() => sendMonthlyReportNow(ownerTokenInput)}
                >
                  {sendingMonthlyReport ? "Gönderiliyor…" : "Gönder"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      

      <div className="settings-card">
        <div className="settings-card-title"><IcCart size={17} /> Sipariş Alma</div>
        <div className="settings-form">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="ordering-enabled"
              checked={orderingEnabled}
              onChange={e => setOrderingConfirm({ val: e.target.checked })}
            />
            <label htmlFor="ordering-enabled">Sistem üzerinden sipariş alma aktif</label>
          </div>
          {orderingMsg && <div className={`settings-msg${orderingMsg.err ? " err" : " ok"}`}>{orderingMsg.text}</div>}
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card-title"><IcMonitor size={17} /> Menü Görünümü</div>
        <div className="settings-form">
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            {[["classic", "Klasik", "Kategori ızgarası"], ["premium", "Premium", "Tek akış · hikâye"]].map(([val, title, sub]) => (
              <button key={val} type="button" onClick={() => menuStyle !== val && setMenuStyleConfirm(val)}
                style={{
                  flex: 1, textAlign: "left", cursor: "pointer", borderRadius: 12, padding: "12px 14px",
                  border: menuStyle === val ? "2px solid var(--mint)" : "1px solid var(--border)",
                  background: menuStyle === val ? "rgba(var(--mint-rgb),0.08)" : "var(--surface)",
                  color: "var(--text)",
                }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
                <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>{sub}</div>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 8 }}>
            Müşterinin karekod menüde göreceği görünüm. Değişiklik anında yansır.
          </div>
          {menuStyleMsg && <div className={`settings-msg${menuStyleMsg.err ? " err" : " ok"}`}>{menuStyleMsg.text}</div>}

          <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Kafe Hero Görseli <span style={{ color: "var(--text3)", fontWeight: 400 }}>(premium menü üst görseli)</span>
            </div>
            {heroImage ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <img src={`${API}${heroImage}`} alt="hero" style={{ width: 96, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }} />
                <button type="button" onClick={removeHero}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text2)", cursor: "pointer", fontSize: 13 }}>
                  Kaldır
                </button>
              </div>
            ) : (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, border: "1.5px dashed var(--border)", background: "transparent", color: "var(--text2)", cursor: "pointer", fontSize: 13.5 }}>
                {heroUploading ? "Yükleniyor…" : "＋ Görsel Yükle"}
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
                  disabled={heroUploading} onChange={e => uploadHero(e.target.files)} />
              </label>
            )}
            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 8 }}>
              Kafenin mekân fotoğrafı. Yüklenmezse hero degrade zemin kullanır. (jpg/png/webp, max 5 MB)
            </div>
            {heroMsg && <div className={`settings-msg${heroMsg.err ? " err" : " ok"}`}>{heroMsg.text}</div>}
          </div>
        </div>
      </div>

      <WorkingHoursCard />

      {/* ── Ordering Confirm Modal ── */}
      {menuStyleConfirm && (
        <div style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,
          backdropFilter:"blur(3px)"
        }}>
          <div style={{
            background:"var(--card,#fff)",borderRadius:20,padding:"36px 36px 28px",
            maxWidth:440,width:"92%",boxShadow:"0 12px 40px rgba(0,0,0,0.25)",
            border:"1px solid var(--border)"
          }}>
            <div style={{fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:12}}>
              {menuStyleConfirm === "premium" ? "Premium Menüye Geç" : "Klasik Menüye Geç"}
            </div>
            <div style={{fontSize:15,color:"var(--text2)",lineHeight:1.7,marginBottom:30}}>
              {menuStyleConfirm === "premium"
                ? "Müşteriler karekod menüde premium (tek-akış) görünümü görecek. Emin misiniz?"
                : "Müşteriler karekod menüde klasik (kategori ızgarası) görünümü görecek. Emin misiniz?"}
            </div>
            <div style={{display:"flex",gap:12,justifyContent:"flex-end"}}>
              <button onClick={() => setMenuStyleConfirm(null)}
                style={{background:"transparent",border:"1px solid var(--border)",borderRadius:10,padding:"12px 24px",fontSize:15,color:"var(--text2)",cursor:"pointer"}}>İptal</button>
              <button onClick={() => { saveMenuStyle(menuStyleConfirm); setMenuStyleConfirm(null); }}
                style={{background:"var(--mint)",border:"none",borderRadius:10,padding:"12px 28px",fontSize:15,fontWeight:600,color:"#fff",cursor:"pointer"}}>Evet, Onayla</button>
            </div>
          </div>
        </div>
      )}

      {orderingConfirm && (
        <div style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,
          backdropFilter:"blur(3px)"
        }}>
          <div style={{
            background:"var(--card,#fff)",borderRadius:20,padding:"36px 36px 28px",
            maxWidth:440,width:"92%",boxShadow:"0 12px 40px rgba(0,0,0,0.25)",
            border:"1px solid var(--border)"
          }}>
            <div style={{fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:12}}>
              {orderingConfirm.val ? "Sipariş Almayı Aç" : "Sipariş Almayı Kapat"}
            </div>
            <div style={{fontSize:15,color:"var(--text2)",lineHeight:1.7,marginBottom:30}}>
              {orderingConfirm.val
                ? "Sistem üzerinden sipariş alma aktif edilecektir. Emin misiniz?"
                : "Sistem üzerinden sipariş alma pasif edilecektir. Emin misiniz?"}
            </div>
            <div style={{display:"flex",gap:12,justifyContent:"flex-end"}}>
              <button
                onClick={() => setOrderingConfirm(null)}
                style={{
                  background:"transparent",border:"1px solid var(--border)",
                  borderRadius:10,padding:"12px 24px",fontSize:15,
                  color:"var(--text2)",cursor:"pointer"
                }}
              >İptal</button>
              <button
                onClick={() => {
                  setOrderingEnabled(orderingConfirm.val);
                  saveOrderingEnabled(orderingConfirm.val);
                  setOrderingConfirm(null);
                }}
                style={{
                  background:"var(--mint)",border:"none",borderRadius:10,
                  padding:"12px 28px",fontSize:15,fontWeight:600,
                  color:"#fff",cursor:"pointer"
                }}
              >Evet, Onayla</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Şifre Değiştir ── */}
      <div className="settings-card">
        <div className="settings-card-title">
          <IcKey size={15} /> Şifremi Değiştir
          {user && (
            <span style={{ fontSize: "12px", fontWeight: 500, color: "var(--text-muted, #888)", marginLeft: "8px" }}>
              ({user.username} — {user.role === "admin" ? "Admin" : "Barista"})
            </span>
          )}
        </div>
        <div className="settings-form">
          <div className="input-group-v">
            <label>Mevcut Şifre</label>
            <input
              type="password"
              value={curPw}
              onChange={e => { setCurPw(e.target.value); setPwMsg(null); }}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          <div className="input-group-v">
            <label>Yeni Şifre</label>
            <input
              type="password"
              value={newPw}
              onChange={e => { setNewPw(e.target.value); setPwMsg(null); }}
              placeholder="En az 6 karakter"
              autoComplete="new-password"
            />
          </div>
          <div className="input-group-v">
            <label>Yeni Şifre (Tekrar)</label>
            <input
              type="password"
              value={newPw2}
              onChange={e => { setNewPw2(e.target.value); setPwMsg(null); }}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>
          {pwMsg && <div className={`settings-msg${pwMsg.err ? " err" : " ok"}`}>{pwMsg.text}</div>}
          <button
            className="btn-save"
            disabled={pwBusy || !curPw || !newPw || !newPw2}
            onClick={() => {
              if (newPw !== newPw2) { setPwMsg({ err: true, text: "Yeni şifreler eşleşmiyor" }); return; }
              if (newPw.length < 6)  { setPwMsg({ err: true, text: "Yeni şifre en az 6 karakter olmalı" }); return; }
              setPwMsg(null);
              setPwConfirm(true);
            }}
          >{pwBusy ? "Kaydediliyor…" : "Şifreyi Güncelle"}</button>
        </div>
      </div>

      {/* ── Şifre Değiştirme Onay Modalı ── */}
      {pwConfirm && (
        <div className="modal-overlay" onClick={() => !pwBusy && setPwConfirm(false)}>
          <div className="pw-confirm-card" onClick={e => e.stopPropagation()}>
            <div className="pw-confirm-icon"><IcKey size={28} /></div>
            <div className="pw-confirm-title">Şifreyi Değiştir</div>
            <div className="pw-confirm-body">
              Şifrenizi güncellemek istediğinizden emin misiniz?
            </div>
            <div className="pw-confirm-actions">
              <button
                className="btn-secondary"
                disabled={pwBusy}
                onClick={() => setPwConfirm(false)}
              >İptal</button>
              <button
                className="btn-save"
                disabled={pwBusy}
                onClick={async () => {
                  setPwBusy(true);
                  try {
                    const res = await fetch(`${API}/api/auth/change-password`, {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ current_password: curPw, new_password: newPw }),
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) {
                      setPwMsg({ err: true, text: data.detail || "Hata oluştu" });
                    } else {
                      setPwMsg({ err: false, text: "Şifre başarıyla değiştirildi ✓" });
                      setCurPw(""); setNewPw(""); setNewPw2("");
                    }
                  } catch {
                    setPwMsg({ err: true, text: "Bağlantı hatası" });
                  }
                  setPwBusy(false);
                  setPwConfirm(false);
                }}
              >{pwBusy ? "Güncelleniyor…" : "Evet, Değiştir"}</button>
            </div>
          </div>
        </div>
      )}

      <div className="settings-card">
        <div className="settings-card-title"><IcInfo size={17} /> Sistem Bilgisi</div>
        <div className="settings-info">
          <div className="si-row"><span>API</span><span>{API || "(proxy)"}</span></div>
          <div className="si-row"><span>Versiyon</span><span>LikapAI Cafe OS v2.1</span></div>
        </div>
      </div>
    </div>
  );
}

// ── Feedbacks Tab ─────────────────────────────────────────────────────────────
const STAR_COLORS = { 5: "#f5a623", 4: "#f5a623", 3: "#f5a623", 2: "#e57373", 1: "#e57373" };

// ── 2-adım silme modalı (FeedbacksTab için) ───────────────────────────────────
function FeedbackDeleteModal({ onConfirm, onClose }) {
  const [step, setStep]         = useState(1);
  const [pinInput, setPinInput] = useState("");
  const [error, setError]       = useState("");
  const [busy, setBusy]         = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await onConfirm(pinInput);
    } catch (e) {
      setError(e.message || "Bir hata oluştu");
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14,
        padding: "24px 28px", maxWidth: 380, width: "90%",
      }}>
        {step === 1 ? (
          <>
            <div style={{ fontWeight: 700, color: "var(--red, #e57373)", fontSize: 16, marginBottom: 12, display:"inline-flex", alignItems:"center", gap:8 }}>
              <IcWarning size={17} /> Tüm Değerlendirmeleri Sil
            </div>
            <div style={{ color: "var(--text2)", fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              Tüm müşteri değerlendirmeleri (adisyon QR + masa QR) kalıcı olarak silinecek.
              Bu işlem geri alınamaz.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={onClose} style={{
                background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8,
                color: "var(--text1)", padding: "9px 18px", cursor: "pointer", fontSize: 14,
              }}>İptal</button>
              <button onClick={() => setStep(2)} style={{
                background: "#7f1d1d", border: "1px solid #e57373", borderRadius: 8,
                color: "#e57373", padding: "9px 18px", cursor: "pointer", fontSize: 14, fontWeight: 700,
              }}>Evet, Devam Et</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 700, color: "var(--text1)", fontSize: 15, marginBottom: 16 }}>
              <IcLock size={17} /> Admin PIN Doğrulama
            </div>
            <input
              type="password"
              value={pinInput}
              onChange={e => setPinInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !busy && pinInput && handleDelete()}
              placeholder="Admin PIN"
              autoFocus
              style={{
                width: "100%", background: "var(--surface2)", border: "1.5px solid var(--border)",
                borderRadius: 8, color: "var(--text1)", padding: "10px 14px",
                fontSize: 16, marginBottom: 10, letterSpacing: 4, boxSizing: "border-box",
              }}
            />
            {error && (
              <div style={{ color: "#e57373", fontSize: 13, marginBottom: 10 }}>{error}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setStep(1); setPinInput(""); setError(""); }} style={{
                background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8,
                color: "var(--text1)", padding: "9px 18px", cursor: "pointer", fontSize: 14,
              }}>Geri</button>
              <button onClick={handleDelete} disabled={busy || !pinInput} style={{
                background: "#7f1d1d", border: "1px solid #e57373", borderRadius: 8,
                color: "#e57373", padding: "9px 18px", fontSize: 14, fontWeight: 700,
                opacity: (busy || !pinInput) ? 0.5 : 1,
                cursor: (busy || !pinInput) ? "not-allowed" : "pointer",
              }}>
                {busy ? "Siliniyor..." : <span style={{display:"inline-flex",alignItems:"center",gap:6,justifyContent:"center"}}><IcTrash size={15} /> Tümünü Sil</span>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FeedbacksTab() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filterRating, setFilterRating] = useState("");
  const [loading, setLoading]     = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [resG, resS] = await Promise.all([
        fetch(`${API}/api/admin/general-feedbacks?limit=200`, { credentials: 'include' }),
        fetch(`${API}/api/admin/feedbacks?limit=200`,         { credentials: 'include' }),
      ]);
      const [dG, dS] = await Promise.all([resG.json(), resS.json()]);
      const general = (dG.feedbacks || []).map(f => ({ ...f, _src: "masa" }));
      const session = (dS.feedbacks || []).map(f => ({ ...f, _src: "adisyon" }));
      const merged  = [...general, ...session].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setFeedbacks(merged);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDeleteAll = async (_pinInput) => {
    const res = await fetch(`${API}/api/feedback/all`, {
      method: "DELETE",
      credentials: 'include',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    setShowDeleteModal(false);
    load();
  };

  const stars   = (n) => "★".repeat(n) + "☆".repeat(5 - n);
  const visible = filterRating
    ? feedbacks.filter(f => f.rating === Number(filterRating))
    : feedbacks;
  const total   = visible.length;
  const avg     = total ? (visible.reduce((s, f) => s + f.rating, 0) / total).toFixed(1) : null;

  return (
    <div className="tab-content">
      {showDeleteModal && (
        <FeedbackDeleteModal
          onConfirm={handleDeleteAll}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      <SectionTitle action={
        <button
          onClick={() => setShowDeleteModal(true)}
          style={{
            background: "transparent", border: "1px solid #555", borderRadius: 7,
            color: "#777", padding: "5px 12px", cursor: "pointer", fontSize: 12,
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          <IcTrash size={14} /> Tümünü Sil
        </button>
      }>Müşteri Değerlendirmeleri</SectionTitle>

      {/* Özet */}
      <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <div className="settings-card" style={{flex:1,minWidth:140,padding:"16px 20px",textAlign:"center"}}>
          <div style={{fontSize:32,fontWeight:800,color:"var(--mint)"}}>{total}</div>
          <div style={{fontSize:12,color:"var(--text3)",marginTop:4}}>Toplam Değerlendirme</div>
        </div>
        <div className="settings-card" style={{flex:1,minWidth:140,padding:"16px 20px",textAlign:"center"}}>
          <div style={{fontSize:32,fontWeight:800,color:"#f5a623"}}>{avg ?? "—"}</div>
          <div style={{fontSize:12,color:"var(--text3)",marginTop:4}}>Ortalama Puan</div>
        </div>
      </div>

      {/* Filtre */}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        {["","5","4","3","2","1"].map(r => (
          <button key={r} onClick={() => setFilterRating(r)} style={{
            padding:"6px 14px", borderRadius:20, border:"1.5px solid",
            borderColor: filterRating === r ? "var(--mint,#4E7C62)" : "var(--border)",
            background:  filterRating === r ? "var(--mint,#4E7C62)" : "var(--surface)",
            color:       filterRating === r ? "#fff" : "var(--text1)",
            fontSize:13, cursor:"pointer", fontWeight: filterRating === r ? 700 : 400,
          }}>
            {r ? `${"★".repeat(Number(r))} ${r} Yıldız` : "Tümü"}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading
        ? <div className="tab-loading">Yükleniyor…</div>
        : visible.length === 0
          ? <div style={{color:"var(--text3)",padding:"32px 0",textAlign:"center"}}>Henüz değerlendirme yok.</div>
          : visible.map((f, i) => (
            <div key={`${f._src}-${f.id}-${i}`} className="settings-card" style={{marginBottom:10,padding:"14px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:20,color:STAR_COLORS[f.rating]||"#f5a623",letterSpacing:2}}>
                    {stars(f.rating)}
                  </span>
                  <span style={{
                    fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
                    padding:"2px 8px",borderRadius:10,
                    background: f._src === "masa" ? "#e8f4fd" : "#f0f0f5",
                    color:      f._src === "masa" ? "#1a6fa8" : "#666",
                  }}>
                    {f._src === "masa" ? "Masa QR" : (f.table_label || `#${f.session_id}`)}
                  </span>
                </div>
                <span style={{fontSize:11,color:"var(--text3)",whiteSpace:"nowrap"}}>
                  {f.created_at?.slice(0,16).replace("T"," ")}
                </span>
              </div>
              {f.tags?.length > 0 && (
                <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:6}}>
                  {f.tags.map(t => (
                    <span key={t} style={{
                      background:"var(--bg2)",border:"1px solid var(--border)",
                      borderRadius:12,padding:"3px 10px",fontSize:12,color:"var(--text2)"
                    }}>{t}</span>
                  ))}
                </div>
              )}
              {f.comment && (
                <div style={{marginTop:8,fontSize:13,color:"var(--text2)",fontStyle:"italic"}}>
                  "{f.comment}"
                </div>
              )}
            </div>
          ))
      }
    </div>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────────
const EVENT_TYPES = [
  "ORDER_CREATED",
  "ORDER_STATUS_CHANGED",
  "PAYMENT_PROCESSED",
  "SESSION_FULLY_PAID",
  "SESSION_CLOSED",
];

const LOG_TYPE_COLORS = {
  ORDER_CREATED:        "mint",
  ORDER_STATUS_CHANGED: "lavender",
  PAYMENT_PROCESSED:    "green",
  SESSION_FULLY_PAID:   "green",
  SESSION_CLOSED:       "text3",
};

function payloadSummary(payload) {
  if (!payload || typeof payload !== "object") return "—";
  const parts = [];
  if (payload.table)         parts.push(`masa: ${payload.table}`);
  if (payload.amount != null) parts.push(`₺${payload.amount}`);
  if (payload.method)        parts.push(payload.method);
  if (payload.from_status && payload.to_status)
    parts.push(`${payload.from_status} → ${payload.to_status}`);
  if (payload.total != null && !parts.length)
    parts.push(`₺${payload.total}`);
  return parts.length ? parts.join(" · ") : JSON.stringify(payload).slice(0, 60);
}

function LogsTab() {
  const LIMIT = 50;
  const [logs,      setLogs]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [offset,    setOffset]    = useState(0);
  const [typeFilter,setTypeFilter]= useState("");
  const [sessionQ,  setSessionQ]  = useState("");

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (typeFilter) params.set("type", typeFilter);
      const sid = parseInt(sessionQ, 10);
      if (!isNaN(sid) && sessionQ.trim()) params.set("session_id", sid);

      const res = await fetch(`${API}/api/admin/events?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setLogs(data.events);
      setTotal(data.total);
      setOffset(off);
    } catch (e) {
      console.error("Events fetch failed", e);
    }
    setLoading(false);
  }, [typeFilter, sessionQ]);

  useEffect(() => { load(0); }, [load]);

  const fmtTime = (ts) => {
    if (!ts) return "—";
    try {
      const d = new Date(ts.replace(" ", "T") + "Z");
      return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch { return ts; }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="tab-content">
      <SectionTitle><IcClipboard size={17} /> Olay Günlüğü</SectionTitle>

      {/* Filters */}
      <div className="log-filter-bar">
        <div className="filter-bar">
          <button
            className={`filter-btn${typeFilter === "" ? " active" : ""}`}
            onClick={() => setTypeFilter("")}
          >Tümü</button>
          {EVENT_TYPES.map(t => (
            <button
              key={t}
              className={`filter-btn${typeFilter === t ? " active" : ""}`}
              onClick={() => setTypeFilter(t)}
            >
              {t.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <div className="log-search-row">
          <div className="input-group" style={{ maxWidth: 200 }}>
            <span>#</span>
            <input
              type="number"
              placeholder="Oturum ID"
              value={sessionQ}
              onChange={e => setSessionQ(e.target.value)}
            />
          </div>
          <button className="filter-btn" onClick={() => load(0)}><IcSearch size={15} /> Ara</button>
        </div>
      </div>

      {/* Count */}
      <div className="log-count">{total} kayıt</div>

      {loading ? (
        <div className="tab-loading">Yükleniyor…</div>
      ) : (
        <>
          {/* Table */}
          <div className="log-table">
            <div className="lt-header">
              <span>Zaman</span>
              <span>Olay</span>
              <span>Oturum</span>
              <span>Sipariş</span>
              <span>Özet</span>
            </div>
            {logs.length === 0 && (
              <div className="lt-empty">Kayıt bulunamadı</div>
            )}
            {logs.map(ev => (
              <div key={ev.id} className="lt-row">
                <span className="lt-time">{fmtTime(ev.created_at)}</span>
                <span className={`log-type-badge lt-${LOG_TYPE_COLORS[ev.type] || "text3"}`}>
                  {ev.type.replace(/_/g, " ")}
                </span>
                <span className="lt-id">
                  {ev.payload?.session_id != null ? `#${ev.payload.session_id}` : "—"}
                </span>
                <span className="lt-id">
                  {ev.payload?.order_id != null ? `#${ev.payload.order_id}` : "—"}
                </span>
                <span className="lt-summary">{payloadSummary(ev.payload)}</span>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="log-pagination">
              <button
                className="filter-btn"
                disabled={offset === 0}
                onClick={() => load(Math.max(0, offset - LIMIT))}
              >← Önceki</button>
              <span className="log-page-info">{currentPage} / {totalPages}</span>
              <button
                className="filter-btn"
                disabled={offset + LIMIT >= total}
                onClick={() => load(offset + LIMIT)}
              >Sonraki →</button>
            </div>
          )}
        </>
      )}

      {/* ── Denetim Günlüğü ── */}
      <AuditLogSection />
    </div>
  );
}

// ── Audit Log Section ─────────────────────────────────────────────────────────
const AUDIT_ACTION_LABELS = {
  LOGIN:             { label: "Giriş",              cls: "ev-green"   },
  LOGOUT:            { label: "Çıkış",              cls: "ev-neutral" },
  PASSWORD_CHANGED:  { label: "Şifre Değişti",      cls: "ev-amber"   },
  ORDER_CREATED:     { label: "Sipariş Oluştu",     cls: "ev-green"   },
  ORDER_EDITED:      { label: "Sipariş Düzenlendi", cls: "ev-amber"   },
  ORDER_CANCELLED:   { label: "Sipariş İptal",      cls: "ev-red"     },
  PAYMENT_PROCESSED: { label: "Ödeme Alındı",       cls: "ev-teal"    },
  SESSION_CLOSED:    { label: "Masa Kapandı",       cls: "ev-blue"    },
  SESSION_REOPENED:  { label: "Masa Açıldı",        cls: "ev-rose"    },
  PRICE_CHANGED:     { label: "Fiyat Değişti",      cls: "ev-amber"   },
  PRODUCT_CREATED:   { label: "Ürün Eklendi",       cls: "ev-mint"    },
  PRODUCT_DELETED:   { label: "Ürün Silindi",       cls: "ev-red"     },
  TABLE_CREATED:     { label: "Masa Eklendi",       cls: "ev-mint"    },
  TABLE_UPDATED:     { label: "Masa Güncellendi",   cls: "ev-amber"   },
  TABLE_DELETED:     { label: "Masa Silindi",       cls: "ev-red"     },
  USER_CREATED:      { label: "Kullanıcı Eklendi",  cls: "ev-mint"    },
};

function auditDetails(action, details) {
  if (!details) return "";
  if (action === "PRICE_CHANGED")
    return `${details.product}: ₺${details.old_price} → ₺${details.new_price}`;
  if (action === "ORDER_CANCELLED" || action === "ORDER_CREATED" || action === "ORDER_EDITED")
    return `${details.table || ""} · ₺${details.total ?? details.new_total ?? ""}`.replace(/^·\s*/, "");
  if (action === "PAYMENT_PROCESSED")
    return `${details.table || ""} · ₺${details.amount || ""} ${details.method ? `(${details.method})` : ""}`.trim().replace(/^·\s*/, "");
  if (action === "SESSION_CLOSED" || action === "SESSION_REOPENED")
    return `${details.table || ""} · ₺${details.total_amount || ""}`.replace(/^·\s*/, "");
  if (action === "PRODUCT_CREATED")
    return `${details.name || ""} · ₺${details.price ?? ""}`.replace(/^·\s*/, "");
  if (action === "PRODUCT_DELETED")
    return details.name || "";
  if (action === "TABLE_CREATED" || action === "TABLE_DELETED" || action === "TABLE_UPDATED")
    return `${details.label || details.code || ""}`;
  return "";
}

function AuditLogSection() {
  const LIMIT = 50;
  const [logs,    setLogs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset,  setOffset]  = useState(0);
  const [filter,  setFilter]  = useState("");

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (filter) params.set("action", filter);
      const res = await fetch(`${API}/api/admin/audit-logs?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setOffset(off);
    } catch (e) { console.error("Audit fetch failed", e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(0); }, [load]);

  const fmtTime = (ts) => {
    if (!ts) return "—";
    try {
      const d = new Date(ts.replace(" ", "T") + "Z");
      return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return ts; }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;
  const ACTIONS = Object.keys(AUDIT_ACTION_LABELS);

  return (
    <>
      <SectionTitle style={{ marginTop: 32 }}><IcSearch size={17} /> Denetim Günlüğü</SectionTitle>

      <div className="log-filter-bar">
        <div className="filter-bar">
          <button className={`filter-btn${filter === "" ? " active" : ""}`} onClick={() => setFilter("")}>Tümü</button>
          {ACTIONS.map(a => (
            <button key={a} className={`filter-btn${filter === a ? " active" : ""}`} onClick={() => setFilter(a)}>
              {AUDIT_ACTION_LABELS[a].label}
            </button>
          ))}
        </div>
      </div>

      <div className="log-count">{total} kayıt</div>

      {loading ? (
        <div className="tab-loading">Yükleniyor…</div>
      ) : (
        <>
          <div className="log-table">
            <div className="lt-header">
              <span>Zaman</span>
              <span>Aksiyon</span>
              <span>Kullanıcı</span>
              <span>Detay</span>
              <span>IP</span>
            </div>
            {logs.length === 0 && <div className="lt-empty">Kayıt bulunamadı</div>}
            {logs.map(log => {
              const meta = AUDIT_ACTION_LABELS[log.action] || { label: log.action, cls: "ev-neutral" };
              return (
                <div key={log.id} className="lt-row">
                  <span className="lt-time">{fmtTime(log.created_at)}</span>
                  <span className={`log-type-badge lt-${meta.cls.replace("ev-", "")}`}>{meta.label}</span>
                  <span className="lt-summary" style={{ fontWeight: 500 }}>{log.username}</span>
                  <span className="lt-summary">{auditDetails(log.action, log.details)}</span>
                  <span className="lt-time" style={{ fontSize: 11 }}>{log.ip_address || "—"}</span>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="log-pagination">
              <button className="filter-btn" disabled={offset === 0} onClick={() => load(Math.max(0, offset - LIMIT))}>← Önceki</button>
              <span className="log-page-info">{currentPage} / {totalPages}</span>
              <button className="filter-btn" disabled={offset + LIMIT >= total} onClick={() => load(offset + LIMIT)}>Sonraki →</button>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Sessions Tab (Closed Sessions / Reopen) ───────────────────────────────────
function SessionsTab() {
  const [sessions,    setSessions]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [reopenId,    setReopenId]    = useState(null);
  const [reason,      setReason]      = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [msg,         setMsg]         = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/sessions?status=CLOSED&limit=50`, {
      credentials: 'include',
    })
      .then(r => r.ok ? r.json() : { sessions: [] })
      .then(d => setSessions(d.sessions || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const doReopen = async (sid) => {
    if (!reason.trim()) return;
    setSubmitting(true);
    setMsg(null);
    try {
      const res = await fetch(`${API}/api/sessions/${sid}/reopen`, {
        method:  "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ reason: reason.trim() }),
      });
      if (res.ok) {
        setMsg({ err: false, text: "✓ Oturum başarıyla geri açıldı." });
        setReopenId(null);
        setReason("");
        load();
      } else {
        const d = await res.json();
        setMsg({ err: true, text: d.detail || "Hata oluştu." });
      }
    } catch {
      setMsg({ err: true, text: "Bağlantı hatası." });
    }
    setSubmitting(false);
  };

  const fmt = (ts) => ts
    ? new Date(ts).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })
    : "—";

  return (
    <div className="admin-content">
      <SectionTitle><IcFolder size={17} /> Kapalı Oturumlar</SectionTitle>

      {msg && (
        <div className={`csr-msg${msg.err ? " err" : " ok"}`} onClick={() => setMsg(null)}>
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="admin-loading">Yükleniyor…</div>
      ) : sessions.length === 0 ? (
        <div className="admin-empty">
          <span className="admin-empty-icon"><IcFolder size={44} /></span>
          <span className="admin-empty-title">Kapalı oturum yok</span>
          <span className="admin-empty-sub">Tamamlanan oturumlar burada görünür.</span>
        </div>
      ) : (
        <div className="csr-list">
          {sessions.map(s => (
            <div key={s.id} className={`csr-row${reopenId === s.id ? " active" : ""}`}>
              <div className="csr-info">
                <span className="csr-table">{s.table_label}</span>
                <span className="csr-dates">
                  {fmt(s.opened_at)} → {fmt(s.closed_at)}
                </span>
                <span className="csr-amounts">
                  Toplam&nbsp;<strong>₺{Math.round(s.total_amount)}</strong>
                  &nbsp;·&nbsp;
                  Ödendi&nbsp;<strong>₺{Math.round(s.paid_amount)}</strong>
                  {(s.reopen_count || 0) > 0 && (
                    <span className="csr-reopen-badge">↩ {s.reopen_count}×</span>
                  )}
                </span>
              </div>

              {reopenId !== s.id ? (
                <button
                  className="csr-reopen-btn"
                  onClick={() => { setReopenId(s.id); setReason(""); setMsg(null); }}
                  style={{display:"inline-flex",alignItems:"center",gap:6}}
                >
                  <IcUnlock size={14} /> Geri Aç
                </button>
              ) : (
                <div className="csr-reason-block">
                  <input
                    className="csr-reason-input"
                    placeholder="Sebep yazın (zorunlu)…"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    autoFocus
                    onKeyDown={e => e.key === "Enter" && doReopen(s.id)}
                  />
                  <div className="csr-reason-btns">
                    <button
                      className="btn-save-sm"
                      disabled={!reason.trim() || submitting}
                      onClick={() => doReopen(s.id)}
                    >
                      {submitting ? "…" : "✓ Onayla"}
                    </button>
                    <button
                      className="btn-cancel-sm"
                      onClick={() => { setReopenId(null); setReason(""); }}
                      disabled={submitting}
                    >
                      İptal
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Overpayment Banner ────────────────────────────────────────────────────────
function OverpaymentBanner() {
  const [items, setItems] = useState([]);
  const [open,  setOpen]  = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/overpayments`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { overpayments: [] })
      .then(d => setItems(d.overpayments || []))
      .catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <div className="overpayment-banner">
      <div className="opb-summary" onClick={() => setOpen(o => !o)}>
        <span className="opb-icon"><IcWarning size={18} /></span>
        <span className="opb-text">
          <strong>{items.length}</strong> oturumda fazla ödeme tespit edildi
        </span>
        <span className="opb-toggle">{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="opb-list">
          {items.map(s => (
            <div key={s.id} className="opb-row">
              <span className="opb-table">{s.table_label}</span>
              <span className="opb-meta">
                Ödenen <strong>₺{s.paid_amount.toFixed(2)}</strong>
                {" / "}Toplam <strong>₺{s.total_amount.toFixed(2)}</strong>
              </span>
              <span className="opb-diff">+₺{s.overpayment_diff.toFixed(2)} fazla</span>
            </div>
          ))}
          <div className="opb-note">
            ℹ️ Sistem otomatik iade yapmaz. Manuel işlem gerekir.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Servis Ayarları Tab ────────────────────────────────────────────────────────
function ServisAyarlariTab() {
  const [smtpServer,       setSmtpServer]       = useState("smtp.gmail.com");
  const [smtpPort,         setSmtpPort]         = useState(587);
  const [smtpUser,         setSmtpUser]         = useState("");
  const [smtpPass,         setSmtpPass]         = useState("");
  const [smtpMsg,          setSmtpMsg]          = useState(null);
  const [anthropicKey,     setAnthropicKey]     = useState("");
  const [openaiKey,        setOpenaiKey]        = useState("");
  const [anthropicMasked,  setAnthropicMasked]  = useState("");
  const [openaiMasked,     setOpenaiMasked]     = useState("");
  const [aiMsg,            setAiMsg]            = useState(null);
  const [menuUrl,          setMenuUrl]          = useState("");
  const [menuUrlMsg,       setMenuUrlMsg]       = useState(null);
  const [googleReviewUrl,  setGoogleReviewUrl]  = useState("");
  const [googleMsg,        setGoogleMsg]        = useState(null);
  const [instagramUrl,     setInstagramUrl]     = useState("");
  const [instagramMsg,     setInstagramMsg]     = useState(null);

  useEffect(() => {
    fetch(`${API}/api/admin/smtp-settings`, { credentials: "include" })
      .then(r => r.json()).then(d => {
        setSmtpServer(d.smtp_server || "smtp.gmail.com");
        setSmtpPort(d.smtp_port || 587);
        setSmtpUser(d.smtp_user || "");
      }).catch(() => {});
    fetch(`${API}/api/admin/ai-settings`, { credentials: "include" })
      .then(r => r.json()).then(d => {
        setAnthropicMasked(d.anthropic_masked || "");
        setOpenaiMasked(d.openai_masked || "");
      }).catch(() => {});
    fetch(`${API}/api/admin/settings/menu-url`, { credentials: "include" })
      .then(r => r.json()).then(d => setMenuUrl(d.url || "")).catch(() => {});
    fetch(`${API}/api/admin/settings/google-review-url`, { credentials: "include" })
      .then(r => r.json()).then(d => setGoogleReviewUrl(d.url || "")).catch(() => {});
    fetch(`${API}/api/admin/settings/instagram-url`, { credentials: "include" })
      .then(r => r.json()).then(d => setInstagramUrl(d.url || "")).catch(() => {});
  }, []);

  const saveSMTP = async () => {
    try {
      await fetch(`${API}/api/admin/smtp-settings`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ smtp_server: smtpServer, smtp_port: smtpPort, smtp_user: smtpUser, smtp_pass: smtpPass || undefined }),
      });
      setSmtpMsg({ err: false, text: "SMTP ayarları kaydedildi ✓" });
      setSmtpPass("");
    } catch { setSmtpMsg({ err: true, text: "Kaydetme başarısız" }); }
    setTimeout(() => setSmtpMsg(null), 3000);
  };

  const saveAI = async () => {
    try {
      const body = {};
      if (anthropicKey) body.anthropic_api_key = anthropicKey;
      if (openaiKey)    body.openai_api_key    = openaiKey;
      await fetch(`${API}/api/admin/ai-settings`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setAiMsg({ err: false, text: "AI API key kaydedildi ✓" });
      setAnthropicKey(""); setOpenaiKey("");
      fetch(`${API}/api/admin/ai-settings`, { credentials: "include" })
        .then(r => r.json()).then(d => { setAnthropicMasked(d.anthropic_masked || ""); setOpenaiMasked(d.openai_masked || ""); }).catch(() => {});
    } catch { setAiMsg({ err: true, text: "Kayıt başarısız" }); }
    setTimeout(() => setAiMsg(null), 3000);
  };

  const saveUrl = async (endpoint, val, setMsg) => {
    try {
      const res = await fetch(`${API}${endpoint}?url=${encodeURIComponent(val)}`, { method: "PUT", credentials: "include" });
      setMsg(res.ok ? { err: false, text: "Kaydedildi ✓" } : { err: true, text: "Kaydetme başarısız" });
    } catch { setMsg({ err: true, text: "Bağlantı hatası" }); }
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div className="tab-content">
      <div className="settings-card">
        <div className="settings-card-title"><IcMail size={17} /> E-posta (SMTP) Ayarları</div>
        <div className="settings-form">
          <div className="input-group-v"><label>SMTP Sunucusu</label>
            <input value={smtpServer} onChange={e => setSmtpServer(e.target.value)} placeholder="smtp.gmail.com" /></div>
          <div className="input-group-v"><label>SMTP Port</label>
            <input type="number" value={smtpPort} onChange={e => setSmtpPort(Number(e.target.value))} /></div>
          <div className="input-group-v"><label>E-posta Adresi (Gönderen)</label>
            <input type="email" value={smtpUser} onChange={e => setSmtpUser(e.target.value)} placeholder="senin@gmail.com" /></div>
          <div className="input-group-v"><label>App Password (Gmail)</label>
            <input type="password" value={smtpPass} onChange={e => setSmtpPass(e.target.value)} placeholder="xxxx xxxx xxxx xxxx" />
            <small style={{fontSize:"11px",color:"var(--text3)"}}>Gmail App Password: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{color:"var(--mint)"}}>myaccount.google.com/apppasswords</a></small>
          </div>
          {smtpMsg && <div className={`settings-msg${smtpMsg.err ? " err" : " ok"}`}>{smtpMsg.text}</div>}
          <button className="btn-save" onClick={saveSMTP}>SMTP Ayarlarını Kaydet</button>
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card-title"><IcSparkle size={17} /> AI API Key</div>
        <div className="settings-form">
          <div className="input-group-v"><label>Anthropic API Key</label>
            <input type="password" value={anthropicKey} onChange={e => setAnthropicKey(e.target.value)} placeholder={anthropicMasked || "sk-ant-..."} />
            {anthropicMasked && <small style={{fontSize:"13px",color:"var(--text3)",letterSpacing:"2px"}}>●●●●●●●●●●</small>}
          </div>
          <div className="input-group-v"><label>OpenAI API Key</label>
            <input type="password" value={openaiKey} onChange={e => setOpenaiKey(e.target.value)} placeholder={openaiMasked || "sk-..."} />
            {openaiMasked && <small style={{fontSize:"13px",color:"var(--text3)",letterSpacing:"2px"}}>●●●●●●●●●●</small>}
            <small style={{fontSize:"11px",color:"var(--text3)"}}>İkisi de girilirse Anthropic önceliklidir.</small>
          </div>
          {aiMsg && <div className={`settings-msg${aiMsg.err ? " err" : " ok"}`}>{aiMsg.text}</div>}
          <button className="btn-save" onClick={saveAI}>AI Key Kaydet</button>
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card-title"><IcLink size={17} /> Müşteri Menü URL</div>
        <div className="settings-form">
          <div className="input-group-v"><label>QR Menü Adresi</label>
            <input value={menuUrl} onChange={e => setMenuUrl(e.target.value)} placeholder="https://menu.likapai.app" />
            <small style={{fontSize:"11px",color:"var(--text3)"}}>QR kodlarının yönlendireceği domain. Boşsa backend kendi domain'ini kullanır.</small>
          </div>
          {menuUrlMsg && <div className={`settings-msg${menuUrlMsg.err ? " err" : " ok"}`}>{menuUrlMsg.text}</div>}
          <button className="btn-save" onClick={() => saveUrl("/api/admin/settings/menu-url", menuUrl, setMenuUrlMsg)}>Kaydet</button>
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card-title"><IcStar size={17} /> Google Review Ayarı</div>
        <div className="settings-form">
          <div className="input-group-v"><label>Google Review URL</label>
            <input value={googleReviewUrl} onChange={e => setGoogleReviewUrl(e.target.value)} placeholder="https://g.page/r/XXXXXXXX/review" />
            <small style={{fontSize:"11px",color:"var(--text3)"}}>Google Maps'te kafeni bul → "Yorum Yaz" → linki buraya yapıştır.</small>
          </div>
          {googleMsg && <div className={`settings-msg${googleMsg.err ? " err" : " ok"}`}>{googleMsg.text}</div>}
          <button className="btn-save" onClick={() => saveUrl("/api/admin/settings/google-review-url", googleReviewUrl, setGoogleMsg)}>Kaydet</button>
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card-title"><IcCamera size={17} /> Instagram Ayarı</div>
        <div className="settings-form">
          <div className="input-group-v"><label>Instagram Profil URL</label>
            <input value={instagramUrl} onChange={e => setInstagramUrl(e.target.value)} placeholder="https://instagram.com/kafenizinadi" />
            <small style={{fontSize:"11px",color:"var(--text3)"}}>5 yıldız verilince adisyonda "Instagram'da Takip Et" butonu çıkar.</small>
          </div>
          {instagramMsg && <div className={`settings-msg${instagramMsg.err ? " err" : " ok"}`}>{instagramMsg.text}</div>}
          <button className="btn-save" onClick={() => saveUrl("/api/admin/settings/instagram-url", instagramUrl, setInstagramMsg)}>Kaydet</button>
        </div>
      </div>
    </div>
  );
}

// ── Çalışma Saatleri Kartı ──────────────────────────────────────────────────
const WH_GUNLER = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
const WH_TIMES = (() => { const a = []; for (let h = 0; h < 24; h++) for (const m of ["00", "30"]) a.push((h < 10 ? "0" + h : "" + h) + ":" + m); return a; })();
function WorkingHoursCard() {
  const [days, setDays] = useState(() => WH_GUNLER.map(() => ({ open: "", close: "", closed: false })));
  const [closeOrdering, setCloseOrdering] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/admin/settings/working-hours`)
      .then(r => r.json())
      .then(d => {
        if (d && d.hours && Array.isArray(d.hours.days)) {
          setDays(WH_GUNLER.map((_, i) => ({
            open:   d.hours.days[i]?.open   || "",
            close:  d.hours.days[i]?.close  || "",
            closed: !!d.hours.days[i]?.closed,
          })));
          setCloseOrdering(!!d.hours.close_ordering_when_closed);
        }
      })
      .catch(() => {});
  }, []);

  const setDay = (i, key, val) => setDays(ds => ds.map((d, j) => j === i ? { ...d, [key]: val } : d));

  const save = async () => {
    const payload = {
      days: days.map(d => ({ open: d.open, close: d.close, closed: d.closed })),
      close_ordering_when_closed: closeOrdering,
    };
    try {
      const res = await fetch(`${API}/api/admin/settings/working-hours`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setMsg(res.ok ? { ok: true, text: "Kaydedildi ✓" } : { err: true, text: "Kaydetme başarısız" });
    } catch { setMsg({ err: true, text: "Bağlantı hatası" }); }
    setTimeout(() => setMsg(null), 3000);
  };

  const sel = (closed) => ({
    flex: 1, minWidth: 0, padding: "9px 8px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)",
    opacity: closed ? 0.45 : 1, fontSize: 14, fontFamily: "inherit", textAlign: "center",
    appearance: "none", WebkitAppearance: "none", cursor: closed ? "default" : "pointer",
  });

  return (
    <div className="settings-card">
      <div className="settings-card-title"><IcClock size={17} /> Çalışma Saatleri</div>
      <div className="settings-form">
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 10 }}>
          Boş bırakılırsa menüde saat bölümü hiç görünmez. Gece yarısını geçen kapanış için 00:00 yaz.
        </div>
        <datalist id="wh-times">{WH_TIMES.map(t => <option key={t} value={t} />)}</datalist>
        {WH_GUNLER.map((g, i) => (
          <div key={g} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ width: 82, fontSize: 13, color: "var(--text2)", flexShrink: 0 }}>{g}</span>
            <input type="text" list="wh-times" inputMode="numeric" maxLength={5} placeholder="10:00"
              value={days[i].open} disabled={days[i].closed}
              onChange={e => setDay(i, "open", e.target.value)} style={sel(days[i].closed)} />
            <span style={{ color: "var(--text3)" }}>–</span>
            <input type="text" list="wh-times" inputMode="numeric" maxLength={5} placeholder="10:00"
              value={days[i].close} disabled={days[i].closed}
              onChange={e => setDay(i, "close", e.target.value)} style={sel(days[i].closed)} />
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, flexShrink: 0, cursor: "pointer", color: "var(--text2)" }}>
              <input type="checkbox" checked={days[i].closed}
                onChange={e => setDay(i, "closed", e.target.checked)}
                style={{ accentColor: "var(--mint)", width: 16, height: 16, cursor: "pointer" }} /> Kapalı
            </label>
          </div>
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, fontSize: 13, cursor: "pointer" }}>
          <span style={{ position: "relative", width: 42, height: 24, flexShrink: 0, display: "inline-block" }}>
            <input type="checkbox" checked={closeOrdering} onChange={e => setCloseOrdering(e.target.checked)}
              style={{ opacity: 0, width: 42, height: 24, margin: 0, cursor: "pointer" }} />
            <span style={{ position: "absolute", inset: 0, borderRadius: 24, background: closeOrdering ? "var(--mint)" : "var(--border)", transition: "background .2s", pointerEvents: "none" }} />
            <span style={{ position: "absolute", top: 3, left: closeOrdering ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .2s", pointerEvents: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }} />
          </span>
          <span style={{ lineHeight: 1.4 }}>Kapalıyken sipariş alınmasın <span style={{ color: "var(--text3)" }}>(menü görünür, sepet kapanır)</span></span>
        </label>
        <button className="settings-save-btn" onClick={save}
          style={{ marginTop: 14, background: "var(--mint)", color: "#fff", border: "none", borderRadius: 10, padding: "11px 18px", fontWeight: 600, cursor: "pointer" }}>
          Kaydet
        </button>
        {msg && <div className={`settings-msg${msg.err ? " err" : " ok"}`}>{msg.text}</div>}
      </div>
    </div>
  );
}

// ── Kontrol Merkezi (Sistem + Loglar + Servis Ayarları) ────────────────────────
function KontrolMerkeziTab() {
  const [subTab, setSubTab] = useState("system");

  return (
    <div className="kontrol-wrapper">
      <div className="kontrol-subtabs">
        <button
          className={`kontrol-subtab-btn${subTab === "system" ? " active" : ""}`}
          onClick={() => setSubTab("system")}
        >
          <IcMonitor size={17} /> Sistem Durumu
        </button>
        <button
          className={`kontrol-subtab-btn${subTab === "logs" ? " active" : ""}`}
          onClick={() => setSubTab("logs")}
        >
          <IcClipboard size={17} /> Loglar
        </button>
        <button
          className={`kontrol-subtab-btn${subTab === "servis" ? " active" : ""}`}
          onClick={() => setSubTab("servis")}
        >
          <IcWrench size={17} /> Servis Ayarları
        </button>
      </div>
      <div className="kontrol-subtab-content">
        {subTab === "system" && <SystemStatusTab />}
        {subTab === "logs"   && <LogsTab />}
        {subTab === "servis" && <ServisAyarlariTab />}
      </div>
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers]           = useState([]);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newFullName, setNewFullName] = useState("");
  const [formMsg, setFormMsg]       = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/users`, { credentials: "include" });
      const data = await res.json();
      setUsers(data.users || []);
    } catch { /* sessiz hata */ }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const createBarista = async () => {
    if (!newUsername.trim()) {
      setFormMsg({ err: true, text: "Kullanıcı adı boş olamaz" });
      return;
    }
    if (newPassword.length < 8) {
      setFormMsg({ err: true, text: "Şifre en az 8 karakter olmalı" });
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/users`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: newUsername, password: newPassword, full_name: newFullName }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormMsg({ err: true, text: data.detail || "Hata oluştu" });
        return;
      }
      setFormMsg({ err: false, text: `"${data.username}" hesabı oluşturuldu ✓` });
      setNewUsername(""); setNewPassword(""); setNewFullName("");
      loadUsers();
      setTimeout(() => setFormMsg(null), 3000);
    } catch {
      setFormMsg({ err: true, text: "Bağlantı hatası" });
    }
  };

  const toggleActive = async (userId, isActive) => {
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/toggle-active`, {
        method: "PATCH",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json();
        alert(data.detail || "Hata oluştu");
        return;
      }
      loadUsers();
    } catch { alert("Bağlantı hatası"); }
  };

  const resetPassword = async (userId, username) => {
    const newPw = window.prompt(`"${username}" için yeni şifre (min. 8 karakter):`);
    if (!newPw) return;
    if (newPw.length < 8) { alert("Şifre en az 8 karakter olmalı"); return; }
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/reset-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || "Hata oluştu"); return; }
      setFormMsg({ err: false, text: `"${username}" şifresi sıfırlandı ✓` });
      loadUsers();
      setTimeout(() => setFormMsg(null), 3000);
    } catch { alert("Bağlantı hatası"); }
  };

  return (
    <div className="settings-page">
      <div className="settings-card">
        <div className="settings-card-title"><IcUser size={17} /> Yeni Barista Ekle</div>
        <div className="settings-form">
          <div className="input-group-v">
            <label>Kullanıcı Adı</label>
            <input
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
              placeholder="ahmet"
              autoComplete="off"
            />
          </div>
          <div className="input-group-v">
            <label>Ad Soyad <span style={{ color: "var(--text3)", fontWeight: 400 }}>(opsiyonel)</span></label>
            <input
              value={newFullName}
              onChange={e => setNewFullName(e.target.value)}
              placeholder="Ahmet Yılmaz"
              autoComplete="off"
            />
          </div>
          <div className="input-group-v">
            <label>Geçici Şifre</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="En az 8 karakter"
              autoComplete="new-password"
            />
          </div>
          {formMsg && (
            <div className={`settings-msg${formMsg.err ? " err" : " ok"}`}>{formMsg.text}</div>
          )}
          <button className="btn-save" onClick={createBarista}>Barista Ekle</button>
        </div>
      </div>

      <div className="settings-card">
        <div className="settings-card-title"><IcUsers size={17} /> Mevcut Kullanıcılar</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "8px" }}>
          {users.length === 0 && (
            <div style={{ color: "var(--text3)", fontSize: "13px" }}>Kullanıcı bulunamadı.</div>
          )}
          {users.map(u => (
            <div key={u.id} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 14px", borderRadius: "10px",
              background: "var(--surface)", border: "1px solid var(--border)",
              gap: "12px", flexWrap: "wrap",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: "14px", color: "var(--text1)" }}>
                  {u.username}
                  {u.full_name && u.full_name !== u.username && (
                    <span style={{ fontWeight: 400, color: "var(--text3)", marginLeft: "6px" }}>
                      {u.full_name}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: "6px", marginTop: "4px", flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: "11px", padding: "2px 7px", borderRadius: "20px",
                    background: u.role === "admin" ? "var(--mint)" : "var(--surface2)",
                    color: u.role === "admin" ? "#fff" : "var(--text2)",
                  }}>{u.role}</span>
                  <span style={{
                    fontSize: "11px", padding: "2px 7px", borderRadius: "20px",
                    background: u.is_active ? "#e8f5e9" : "#fce4ec",
                    color: u.is_active ? "#2e7d32" : "#c62828",
                  }}>{u.is_active ? "Aktif" : "Pasif"}</span>
                </div>
                <div style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                  {u.last_login_at
                    ? `Son giriş: ${new Date(u.last_login_at).toLocaleString("tr-TR")}`
                    : "Henüz giriş yapılmadı"}
                </div>
              </div>
              {u.role === "barista" && (
                <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  <button
                    onClick={() => toggleActive(u.id, u.is_active)}
                    style={{
                      fontSize: "12px", padding: "5px 10px", borderRadius: "7px", cursor: "pointer",
                      border: "1px solid var(--border)", background: "var(--bg)",
                      color: u.is_active ? "#c62828" : "#2e7d32",
                    }}
                  >
                    {u.is_active ? "Pasif Yap" : "Aktif Yap"}
                  </button>
                  <button
                    onClick={() => resetPassword(u.id, u.username)}
                    style={{
                      fontSize: "12px", padding: "5px 10px", borderRadius: "7px", cursor: "pointer",
                      border: "1px solid var(--border)", background: "var(--bg)",
                      color: "var(--text2)",
                    }}
                  >
                    Şifre Sıfırla
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── Main Admin ────────────────────────────────────────────────────────────────
export default function Admin({ onBack }) {
  const [tab, setTab] = useState("products");
  const [navOpen, setNavOpen] = useState(false);   // mobil çekmece menü
  useEffect(() => { setNavOpen(false); }, [tab]);  // sekme değişince kapat
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="admin-app">
      <div className="admin-mobile-bar">
        <button className="admin-nav-toggle" onClick={() => setNavOpen(true)} aria-label="Menüyü aç">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <span className="admin-mobile-title">Yönetici Paneli</span>
      </div>
      <div className={`admin-nav-backdrop${navOpen ? " show" : ""}`} onClick={() => setNavOpen(false)} />
      <aside className={`admin-sidebar${navOpen ? " admin-sidebar--open" : ""}`}>
        <div className="admin-logo">
          <span className="admin-logo-icon"><svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2.5c-.7.9-.7 1.7 0 2.6" opacity=".6"/><path d="M11.5 1.8c-.8 1-.8 2 0 3" opacity=".6"/><path d="M15 2.5c-.7.9-.7 1.7 0 2.6" opacity=".6"/><path d="M4 8h12v4.5a5.5 5.5 0 0 1-5.5 5.5h-1A5.5 5.5 0 0 1 4 12.5V8Z"/><path d="M16 9.2h1.8a2.3 2.3 0 0 1 0 4.6H16"/><path d="M3 21h14"/></svg></span>
          <div>
            <div className="admin-logo-name">{localStorage.getItem("coas_cafe_name") || process.env.REACT_APP_CAFE_NAME || ""}</div>
            <div className="admin-logo-sub">Yönetici Paneli</div>
          </div>
        </div>
        {onBack && (
          <button className="admin-back-btn" onClick={onBack}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span>Panele Dön</span>
          </button>
        )}
        <nav className="admin-nav">
          {[
            { id: "tables",    Icon: IcChair,    label: "Masalar"          },
            { id: "products",  Icon: IcBox,      label: "Ürünler"          },
            { id: "sessions",  Icon: IcFolder,   label: "Oturumlar"        },
            { id: "feedbacks", Icon: IcStar,     label: "Değerlendirmeler" },
            { id: "expenses",  Icon: IcMoney,    label: "Finans"           },
            ...(isAdmin ? [{ id: "users", Icon: IcUsers, label: "Kullanıcılar" }] : []),
            { id: "settings",  Icon: IcSettings, label: "Ayarlar"          },
            { id: "kontrol",   Icon: IcMonitor,  label: "Kontrol Merkezi"  },
          ].map(n => (
            <button key={n.id} className={`admin-nav-btn${tab === n.id ? " active" : ""}`} onClick={() => setTab(n.id)}>
              <span className="admin-nav-ic"><n.Icon size={18} /></span><span>{n.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="admin-panel">
        <OverpaymentBanner />
        {tab === "products"  && <ProductsTab />}
        {tab === "tables"    && <TablesTab />}
        {tab === "sessions"  && <SessionsTab />}
        {tab === "feedbacks" && <FeedbacksTab />}
        {tab === "settings"  && <SettingsTab />}
        {tab === "expenses"  && <ExpensesTab />}
        {tab === "kontrol"   && <KontrolMerkeziTab />}
        {tab === "users"     && isAdmin && <UsersTab />}
      </main>
    </div>
  );
}
